/**
 * FurnitureQuiz Web Component
 * 
 * An architecturally robust, multi-path recommendation engine designed to navigate 
 * the high-dimensional complexity of mattress selection. With 10 questions and
 * 2,880,000+ unique answer combinations, this system employs a dynamic scoring strategy
 * to ensure every user receives an optimized, personalized product recommendation.
 * 
 * CORE ARCHITECTURAL SOLUTIONS:
 * 1. Dynamic Flow Management: Questions are non-linear. The `questionFlow` is 
 *    re-evaluated in real-time based on `partnerMapping` (Q2), allowing the quiz 
 *    to skip irrelevant branches and focus on the user's specific context.
 * 2. Asynchronous Multi-Stage Prefetch: To eliminate perceived latency, the engine 
 *    initiates background product discovery during the early question phases.
 * 3. Tag-Based Relevance Scoring: Rather than rigid filtering (which risks zero 
 *    results in complex matrices), we use a weighted overlap algorithm to rank the 
 *    entire catalog against the user's multi-layered preferences.
 * 4. Partner/Parent Synchronization: Provides specialized dual-input handling for 
 *    shared sleeping environments, merging distinct requirements into a unified 
 *    recommendation score.
 * 
 * @author mksd kloc
 */

class FurnitureQuiz extends HTMLElement {
    constructor() {
        super();
        this.currentQuestionIndex = 0;
        this.answers = {};
        this.partnerMapping = null; // Stores Q2 answer: 'me', 'me_and_partner', 'my_parents', 'my_children', 'guests'
        this.questionFlow = []; // Dynamic question flow based on Q2
        this.isInitialRender = true; // Track if this is the first render to prevent auto-scroll on page load
    }

    connectedCallback() {
        this.isPopupMode = this.hasAttribute('data-popup-mode');
        this.init();
        if (this.isPopupMode) {
            this.initPopupMode();
        }
    }

    init() {
        // Parse quiz data from JSON script
        const dataScript = this.querySelector('[data-quiz-data]');
        if (dataScript) {
            try {
                this.quizData = JSON.parse(dataScript.textContent);
            } catch (e) {
                console.error('Quiz data parsing encountered an issue:', e);
                return;
            }
        }

        // Check if form was successfully submitted and show results
        this.checkAndShowResultsAfterSubmission();

        // Initialize answers object
        this.initializeAnswers();

        // Cache DOM elements
        this.questionContainer = this.querySelector('.furniture-quiz-question-container');
        this.prevBtn = this.querySelector('.furniture-quiz-prev-btn');
        this.nextBtn = this.querySelector('.furniture-quiz-next-btn');
        this.progressIndicator = this.querySelector('.furniture-quiz-progress-indicator');

        // Store the original button HTML content (including icon)
        if (this.nextBtn && !this.nextBtn.hasAttribute('data-default-html')) {
            this.nextBtn.setAttribute('data-default-html', this.nextBtn.innerHTML);
        }

        // Pre-starter screen CTA handler
        const preStarterCta = this.querySelector('.furniture-quiz-pre-starter-cta');
        if (preStarterCta) {
            preStarterCta.addEventListener('click', () => this.startWelcomeScreen());
        }

        // Welcome screen CTA handler
        const welcomeCta = this.querySelector('.furniture-quiz-welcome-cta');
        if (welcomeCta) {
            welcomeCta.addEventListener('click', () => this.startQuiz());
        }

        // Initialize question flow - will be updated after Q2 is answered
        this.questionFlow = [];
        if (this.quizData && this.quizData.questions) {
            this.quizData.questions.forEach((q, index) => {
                this.questionFlow.push(q.id || `q${index + 1}`);
            });
        }
        this.currentQuestionIndex = 0;
        this.totalQuestions = this.questionFlow.length;
        const totalEl = this.querySelector('.quiz-total');
        if (totalEl) totalEl.textContent = this.totalQuestions;

        // Render initial question
        this.renderQuestion();
        this.updateNavigation();
    }

    startWelcomeScreen() {
        // Hide pre-starter screen and show welcome screen
        const container = this.querySelector('.furniture-quiz-container');
        if (container) {
            container.classList.remove('quiz-pre-started');
            container.classList.add('quiz-not-started');
        }

        // Capture pre-starter data (optional, but good for tracking)
        const dropdown = this.querySelector('#PreStarterDropdown');
        const input = this.querySelector('#PreStarterInput');
        if (dropdown) this.answers.pre_starter_role = dropdown.value;
        if (input) this.answers.pre_starter_name = input.value;

        // Scroll to top
        setTimeout(() => {
            this.scrollToQuizTop();
        }, 100);
    }

    startQuiz() {
        // Hide welcome screen and show quiz content
        const container = this.querySelector('.furniture-quiz-container');
        const welcomeScreen = this.querySelector('.furniture-quiz-welcome-screen');

        if (container) {
            container.classList.remove('quiz-not-started');
        }
        if (welcomeScreen) {
            welcomeScreen.style.display = 'none';
        }

        // Initialize early product discovery to eliminate latency
        // This leverages the idle time during the user's question journey
        this._prefetchProductsHTML();

        // Scroll to quiz
        setTimeout(() => {
            this.scrollToQuizTop();
        }, 100);
    }

    /**
     * Pre-fetch product HTML as early as possible.
     * The fetch URL (/collections/{base}/machine-learning?view=quiz-api) is static —
     * it returns ALL quiz-eligible products regardless of answers.
     * Scoring happens client-side AFTER fetch, so answers aren't needed for the request.
     *
     * Called when user starts the quiz. By the time they finish 7-9 questions
     * and fill the customer form, the HTML is already in memory.
     */
    _prefetchProductsHTML() {
        if (this._prefetchPromise) return; // already in flight or done

        const basePath = this.quizData.collectionBaseUrl || '/collections/all';
        const cleanBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        const url = `${cleanBasePath}/machine-learning?view=quiz-api`;

        console.log('Pre-fetching quiz products early:', url);

        this._prefetchPromise = fetch(url)
            .then(response => {
                if (!response.ok) {
                    console.warn(`Pre-fetch synchronization issue (${response.status}): ${url}`);
                    this._prefetchPromise = null; // allow discovery retry
                    return null;
                }
                return response.text();
            })
            .then(html => {
                if (html) {
                    this._prefetchedRawHTML = html;
                    console.log('Quiz products pre-fetched and cached ✓');
                }
            })
            .catch(err => {
                console.warn('Pre-fetch discovery pending retry:', err);
                this._prefetchPromise = null; // allow discovery retry
            });
    }

    isPincodePopupOpen() {

        return document.body.classList.contains('popup-pp-open') ||

            !!document.querySelector('.popup-pp--visible:not([hidden])');

    }

    isBlockingUiOpen() {

        // Variant selector / GoKwik side cart / GoKwik checkout / pincode popup
        // (ignoreQuiz: the quiz must not block on its own overlay).
        const D = window.SleepyheadPopup;

        if (D && typeof D.isBlockingUiOpen === 'function') return !!D.isBlockingUiOpen({ ignoreQuiz: true });

        return this.isPincodePopupOpen();

    }



    initPopupMode() {
        const sectionId = this.dataset.sectionId;
        this.popupOverlay = document.getElementById(`QuizPopupOverlay-${sectionId}`);
        this.popupTrigger = document.getElementById(`QuizPopupTrigger-${sectionId}`);

        if (!this.popupOverlay || !this.popupTrigger) return;

        // Session storage check to see if the trigger was permanently closed
        if (sessionStorage.getItem('furniture_quiz_trigger_closed') === 'true') {
            return;
        }

        // Delay logic
        const triggerDelayAttr = this.popupTrigger.dataset.triggerDelay;
        const autoOpenDelayAttr = this.popupTrigger.dataset.autoOpenDelay;

        const triggerDelay = triggerDelayAttr ? parseInt(triggerDelayAttr, 10) * 1000 : 0;
        const autoOpenDelay = autoOpenDelayAttr ? parseInt(autoOpenDelayAttr, 10) * 1000 : 0;

        if (triggerDelay > 0) {
            setTimeout(() => {
                if (sessionStorage.getItem('furniture_quiz_trigger_closed') !== 'true') {
                    this.popupTrigger.style.display = 'block';
                }
            }, triggerDelay);
        } else {
            this.popupTrigger.style.display = 'block';
        }

        if (autoOpenDelay > 0) {
            setTimeout(() => {
                if (sessionStorage.getItem('furniture_quiz_trigger_closed') !== 'true' &&
                    sessionStorage.getItem('furniture_quiz_popup_closed') !== 'true' &&

                    !this.isBlockingUiOpen() &&

                    !this.popupOverlay.classList.contains('active')) {
                    this.openPopup();
                }
            }, autoOpenDelay);
        }

        // Trigger button opens popup
        this.popupTrigger.addEventListener('click', (e) => {
            if (e.target.classList.contains('close-btn')) {
                e.stopPropagation();
                this.closeTrigger();
            } else {
                this.openPopup();
            }
        });

        // Close buttons and backdrop
        this.popupOverlay.querySelectorAll('[data-quiz-popup-close]').forEach(el => {
            el.addEventListener('click', () => this.closePopup());
        });

        // Escape key closes popup
        document.addEventListener('keydown', (e) => {
            if (e.code === 'Escape' && this.popupOverlay.classList.contains('active')) {
                this.closePopup();
            }
        });
    }

    closeTrigger() {
        if (this.popupTrigger) {
            this.popupTrigger.style.display = 'none';
        }
        sessionStorage.setItem('furniture_quiz_trigger_closed', 'true');
        sessionStorage.setItem('furniture_quiz_popup_closed', 'true');
    }

    openPopup() {
        if (!this.popupOverlay) return;

        if (this.isPopupMode && this.isBlockingUiOpen()) return;
        this.popupOverlay.classList.add('active');
        document.body.classList.add('overflow-hidden');

        // Hide the floating trigger button while modal is open
        if (this.popupTrigger) {
            this.popupTrigger.style.display = 'none';
        }

        // Trap focus inside modal if theme utility is available
        if (typeof trapFocus === 'function') {
            const modalDialog = this.popupOverlay.querySelector('.quiz-popup-modal');
            trapFocus(modalDialog, this.popupOverlay.querySelector('.quiz-popup-close-btn'));
        }
    }

    closePopup() {
        if (!this.popupOverlay) return;
        this.popupOverlay.classList.remove('active');
        document.body.classList.remove('overflow-hidden');

        // Prevent auto-open from triggering again in this session
        sessionStorage.setItem('furniture_quiz_popup_closed', 'true');

        // Show the floating trigger button again if user hasn't explicitly closed it
        if (this.popupTrigger && sessionStorage.getItem('furniture_quiz_trigger_closed') !== 'true') {
            this.popupTrigger.style.display = 'block';
        }

        // Release focus trap if theme utility is available
        if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(this.popupTrigger);
        }
    }

    scrollToQuizTop() {
        if (this.isPopupMode) {
            const modalBody = this.closest('.quiz-popup-modal-body');
            if (modalBody) {
                modalBody.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    checkAndShowResultsAfterSubmission() {
        // Check sessionStorage for stored quiz data (e.g. after page refresh)
        try {
            const storedData = sessionStorage.getItem('furniture-quiz-data');
            if (!storedData) return;

            const quizData = JSON.parse(storedData);
            if (!quizData.answers || !quizData.partnerMapping) return;

            // Restore answers and partner mapping for results display
            this.answers = quizData.answers;
            this.partnerMapping = quizData.partnerMapping;

            // Hide quiz container
            const quizContainer = this.querySelector('.furniture-quiz-container');
            if (quizContainer) {
                quizContainer.style.display = 'none';
            }

            // Hide customer form
            const customerForm = this.querySelector('.furniture-quiz-customer-form');
            if (customerForm) {
                customerForm.style.display = 'none';
            }

            // Clear stored data
            sessionStorage.removeItem('furniture-quiz-data');

            // If popup mode, auto-open the modal to show results
            if (this.isPopupMode) {
                this.openPopup();
            }

            // Use stored resultsHTML if available to avoid re-fetching
            if (quizData.resultsHTML) {
                const resultsContainer = document.createElement('div');
                resultsContainer.className = 'furniture-quiz-results-container';
                resultsContainer.innerHTML = quizData.resultsHTML;
                this.appendChild(resultsContainer);

                // Re-bind retake quiz buttons
                resultsContainer.querySelectorAll('[data-retake-quiz]').forEach(btn => {
                    btn.addEventListener('click', () => {
                        this.retakeQuiz();
                    });
                });

                // Re-init scroll and swatch handlers
                this.initProductScroll(resultsContainer);
                this.initSwatchHandlers(resultsContainer);
            } else {
                // Discover recommended products if results are not available in memory
                this.showResults();
            }
        } catch (e) {
            console.error('Historical quiz data retrieval encountered an issue:', e);
        }
    }

    initializeAnswers() {
        // Initialize answer structures dynamically from quiz data
        // Q1 and Q2 are stored as direct values (single select, not conditional)
        // Q3+ are stored as objects keyed by partner mapping
        this.answers = {
            q1: null,
            q2: null,
        };
        if (this.quizData && this.quizData.questions) {
            this.quizData.questions.forEach((q, index) => {
                const qId = q.id || `q${index + 1}`;
                if (qId !== 'q1' && qId !== 'q2') {
                    this.answers[qId] = {};
                }
            });
        }
    }

    getCurrentQuestionId() {
        return this.questionFlow[this.currentQuestionIndex];
    }

    renderQuestion() {
        const questionId = this.getCurrentQuestionId();
        if (!questionId) return;

        const questionData = this.quizData.questions.find(q => q.id === questionId);
        if (!questionData) return;

        // Update image display
        this.updateQuestionImage(questionData);

        // Clear previous question
        if (this.questionContainer) {
            this.questionContainer.innerHTML = '';
        }

        // Build question HTML based on type
        const questionHTML = this.buildQuestionHTML(questionId, questionData);
        if (this.questionContainer) {
            this.questionContainer.innerHTML = questionHTML;
        }

        // Attach option listeners
        this.attachQuestionListeners(questionId, questionData);

        // Attach navigation listeners (NEW)
        this.attachNavigationListeners();

        // Update progress
        this.updateProgress();

        // Scroll to top (but not on initial page load)
        if (!this.isInitialRender) {
            this.scrollToTop();
        } else {
            // Mark that initial render is complete
            this.isInitialRender = false;
        }
        this.updateProgress();
    }

    updateQuestionImage(questionData) {
        // Find all image wrappers and recommendation cards
        const imageWrappers = this.querySelectorAll('.furniture-quiz-image-wrapper');
        const imageSection = this.querySelector('.furniture-quiz-image-section');

        if (!imageSection) return;

        // Remove active class from all images
        imageWrappers.forEach(wrapper => {
            wrapper.classList.remove('active');
        });

        // Remove any existing recommendation cards
        const existingCards = imageSection.querySelectorAll('.furniture-quiz-recommendation-card');
        existingCards.forEach(card => card.remove());

        // Check if this question should show recommendation text instead of image
        if (questionData.showRecommendationText && (questionData.recommendationTextTitle || questionData.recommendationTextBody)) {
            // Show recommendation text card
            const card = document.createElement('div');
            card.className = 'furniture-quiz-recommendation-card active';
            let cardHTML = '';
            if (questionData.recommendationTextTitle) {
                cardHTML += `<div class="sh-h3 furniture-quiz-recommendation-card-title">${questionData.recommendationTextTitle}</div>`;
            }
            if (questionData.recommendationTextBody) {
                cardHTML += `<p class="furniture-quiz-recommendation-card-body">${questionData.recommendationTextBody}</p>`;
            }
            card.innerHTML = cardHTML;
            imageSection.appendChild(card);
        } else {
            // Show image as usual
            const imageIndex = questionData.imageIndex !== undefined ? questionData.imageIndex : this.currentQuestionIndex;
            const currentImageWrapper = this.querySelector(`[data-question-index="${imageIndex}"]`);

            if (currentImageWrapper) {
                currentImageWrapper.classList.add('active');

                // Update background color if specified
                if (questionData.imageBackgroundColor) {
                    currentImageWrapper.style.backgroundColor = questionData.imageBackgroundColor;
                }
            }
        }
    }

    buildQuestionHTML(questionId, questionData) {
        let html = '';

        // Two-column layout: left (question info + recommendation card), right (options)
        html += '<div class="furniture-quiz-question-inner">';

        // LEFT COLUMN: question header, question text, recommendation card
        html += '<div class="furniture-quiz-question-left">';

        //Question number with progressbar 

        html += `


      <div class="furniture-quiz-progress">


        <div class="furniture-quiz-progress-top">


          ${this.quizData.textQuestion || 'Question'} <span class="quiz-current">${this.currentQuestionIndex + 1}</span>  


          ${this.quizData.textOf || 'of'} <span class="quiz-total">${this.totalQuestions}</span>


        </div>


        <div class="furniture-quiz-progress-bar">


          <div class="furniture-quiz-progress-fill" 


            style="width:${((this.currentQuestionIndex + 1) / this.totalQuestions) * 100}%">


          </div>


        </div>


      </div>


    `;

        // Question header with guideline
        html += `


      <div class="furniture-quiz-question-header">


        <div class="furniture-quiz-question-number">${questionData.number}</div>


        <div class="furniture-quiz-question-title">${questionData.title}</div>


        <div class="furniture-quiz-guideline">${this.quizData.guidelineText || 'Guideline'}</div>


      </div>


    `;

        // Question text (varies by partner mapping)
        const questionText = this.getQuestionText(questionId, questionData);
        html += `<div class="furniture-quiz-question-text">${questionText}</div>`;

        // Recommendation / "Why I Ask" card
        const recTitle = questionData.recommendationTextTitle || (this.quizData.whyIAskLabel || 'Why I ask?');
        const recBody = questionData.recommendationTextBody || questionData.whyIAsk || '';
        if (recBody) {
            html += `


        <div class="furniture-quiz-recommendation-card">


          <div class="sh-h3 furniture-quiz-recommendation-card-title">${recTitle}</div>


          <p class="furniture-quiz-recommendation-card-body">${recBody}</p>


        </div>


      `;
        }

        html += '</div>'; // end left column

        // RIGHT COLUMN: options
        html += '<div class="furniture-quiz-question-right">';

        // Options based on question type and partner mapping
        html += this.buildOptionsHTML(questionId, questionData);

        html += `


      <div class="furniture-quiz-navigation-buttons">


        <button class="furniture-quiz-nav-btn furniture-quiz-next-btn">


          ${this.quizData.textNext || 'Next'}


        </button>


        <button class="furniture-quiz-nav-btn furniture-quiz-prev-btn" disabled>


          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" width="14" height="14" x="0" y="0" viewBox="0 0 447.243 447.243" style="enable-background:new 0 0 512 512" xml:space="preserve"><path d="M420.361 192.229a31.967 31.967 0 0 0-5.535-.41H99.305l6.88-3.2a63.998 63.998 0 0 0 18.08-12.8l88.48-88.48c11.653-11.124 13.611-29.019 4.64-42.4-10.441-14.259-30.464-17.355-44.724-6.914a32.018 32.018 0 0 0-3.276 2.754l-160 160c-12.504 12.49-12.515 32.751-.025 45.255l.025.025 160 160c12.514 12.479 32.775 12.451 45.255-.063a32.084 32.084 0 0 0 2.745-3.137c8.971-13.381 7.013-31.276-4.64-42.4l-88.32-88.64a64.002 64.002 0 0 0-16-11.68l-9.6-4.32h314.24c16.347.607 30.689-10.812 33.76-26.88 2.829-17.445-9.019-33.88-26.464-36.71z" fill="#f05a22" opacity="1" data-original="#000000"/></svg> ${this.quizData.textBack || 'Back'}


        </button>


      </div>


    `;

        html += '</div>'; // end right column
        html += '</div>'; // end inner

        return html;
    }

    getQuestionText(questionId, questionData) {
        if (questionId === 'q2') {
            return questionData.text;
        }

        // For Q3-Q10, get text based on partner mapping
        if (!this.partnerMapping) {
            return questionData.text;
        }

        const mappingKey = this.partnerMapping;
        if (questionData.textByMapping && questionData.textByMapping[mappingKey]) {
            return questionData.textByMapping[mappingKey];
        }

        return questionData.text;
    }

    buildOptionsHTML(questionId, questionData) {

        let optionsHTML = '';

        // Q1
        if (questionId === 'q1') {

            optionsHTML += '<div class="furniture-quiz-partner-group">';
            optionsHTML += '<div class="furniture-quiz-partner-options">';

            questionData.options.forEach(option => {

                const isSelected = this.answers.q1 === option.value;

                optionsHTML += `


        <div class="furniture-quiz-option ${isSelected ? 'selected' : ''}" 


          data-option-value="${option.value}"


          data-select-type="single">


          <div class="furniture-quiz-option-text">${option.label}</div>


        </div>


      `;

            });

            optionsHTML += '</div></div>';
        }

        // Q2
        else if (questionId === 'q2') {

            optionsHTML += '<div class="furniture-quiz-partner-group">';
            optionsHTML += '<div class="furniture-quiz-partner-options">';

            questionData.options.forEach(option => {

                const isSelected = this.answers.q2 === option.value;

                optionsHTML += `


        <div class="furniture-quiz-option ${isSelected ? 'selected' : ''}" 


          data-option-value="${option.value}"


          data-select-type="single">


          <div class="furniture-quiz-option-text">${option.label}</div>


        </div>


      `;

            });

            optionsHTML += '</div></div>';
        }

        // Q3–Q10
        else {

            optionsHTML += this.buildConditionalOptionsHTML(questionId, questionData);

        }

        /* -------- AUTO DETECT COLUMN COUNT -------- */

        const groupCount =
            (optionsHTML.match(/furniture-quiz-partner-group/g) || []).length || 1;

        return `


    <div class="furniture-quiz-options-list list-col-${groupCount}">


      ${optionsHTML}


    </div>


  `;
    }

    buildConditionalOptionsHTML(questionId, questionData) {
        let html = '';

        const mappingKey = this.partnerMapping;
        const selectType = questionData.selectType || 'single';
        const isMultiSelect = selectType === 'multi';

        // Get options for current mapping
        let options = questionData.options;
        if (questionData.optionsByMapping && questionData.optionsByMapping[mappingKey]) {
            options = questionData.optionsByMapping[mappingKey];
        }

        // Check if this question has partner/parent inputs
        const hasPartnerInputs = this.hasPartnerInputs(questionId, mappingKey);

        if (hasPartnerInputs) {
            // Build partner/parent inputs
            if (mappingKey === 'me_and_partner') {
                html += this.buildPartnerInputsHTML(questionId, options, 'Me', 'My Partner');
            } else if (mappingKey === 'my_parents') {
                html += this.buildPartnerInputsHTML(questionId, options, 'Parent 1', 'Parent 2');
            }
        } else {
            // Single set of options - wrap in partner-group for consistency
            html += '<div class="furniture-quiz-partner-group">';
            html += '<div class="furniture-quiz-partner-options">';
            options.forEach((option, index) => {
                const optionValue = option.value || option.label;
                let isSelected = false;

                if (isMultiSelect) {
                    const currentAnswers = this.answers[questionId][mappingKey] || [];
                    isSelected = currentAnswers.includes(optionValue);
                } else {
                    isSelected = this.answers[questionId][mappingKey] === optionValue;
                }

                html += `


          <div class="furniture-quiz-option ${isSelected ? 'selected' : ''}" 


               data-option-value="${optionValue}"


               data-select-type="${selectType}">


            <div class="furniture-quiz-option-text">${option.label}</div>


          </div>


        `;
            });
            html += '</div></div>';
        }

        return html;
    }

    buildPartnerInputsHTML(questionId, options, label1, label2) {
        let html = '';
        const selectType = this.quizData.questions.find(q => q.id === questionId)?.selectType || 'single';
        const isMultiSelect = selectType === 'multi';
        const mappingKey = this.partnerMapping;

        // First person inputs
        html += `<div class="furniture-quiz-partner-group">


      <div class="furniture-quiz-partner-label">${label1}</div>


      <div class="furniture-quiz-partner-options">`;

        options.forEach((option, index) => {
            const optionValue = option.value || option.label;
            let isSelected = false;
            const answerKey = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;

            if (isMultiSelect) {
                const currentAnswers = this.answers[questionId][answerKey] || [];
                isSelected = currentAnswers.includes(optionValue);
            } else {
                isSelected = this.answers[questionId][answerKey] === optionValue;
            }

            html += `


        <div class="furniture-quiz-option ${isSelected ? 'selected' : ''}" 


             data-option-value="${optionValue}"


             data-partner-key="${answerKey}"


             data-select-type="${selectType}">


          <div class="furniture-quiz-option-text">${option.label}</div>


        </div>


      `;
        });

        html += `</div></div>`;

        // Second person inputs
        html += `<div class="furniture-quiz-partner-group">


      <div class="furniture-quiz-partner-label">${label2}</div>


      <div class="furniture-quiz-partner-options">`;

        options.forEach((option, index) => {
            const optionValue = option.value || option.label;
            let isSelected = false;
            const answerKey = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

            if (isMultiSelect) {
                const currentAnswers = this.answers[questionId][answerKey] || [];
                isSelected = currentAnswers.includes(optionValue);
            } else {
                isSelected = this.answers[questionId][answerKey] === optionValue;
            }

            html += `


        <div class="furniture-quiz-option ${isSelected ? 'selected' : ''}" 


             data-option-value="${optionValue}"


             data-partner-key="${answerKey}"


             data-select-type="${selectType}">


          <div class="furniture-quiz-option-text">${option.label}</div>


        </div>


      `;
        });

        html += `</div></div>`;

        return html;
    }

    hasPartnerInputs(questionId, mappingKey) {
        // Check if question has hasPartnerInputs property and mapping supports it
        const questionData = this.quizData.questions.find(q => q.id === questionId);
        if (!questionData) return false;

        return questionData.hasPartnerInputs === true &&
            (mappingKey === 'me_and_partner' || mappingKey === 'my_parents');
    }

    shouldSkipQuestion(questionId) {
        if (!this.partnerMapping) return false;

        // Get question data to check skipFor array
        const questionData = this.quizData.questions.find(q => q.id === questionId);
        if (!questionData || !questionData.skipFor) return false;

        // Check if current mapping is in skipFor array
        return questionData.skipFor.includes(this.partnerMapping);
    }

    attachQuestionListeners(questionId, questionData) {
        const options = this.questionContainer.querySelectorAll('.furniture-quiz-option[data-option-value]');

        options.forEach(option => {
            option.addEventListener('click', () => {
                this.handleOptionClick(questionId, option);
            });
        });
    }

    attachNavigationListeners() {

        const prevBtn = this.querySelector('.furniture-quiz-prev-btn');
        const nextBtn = this.querySelector('.furniture-quiz-next-btn');

        // Always show back button (universal back — Q1 goes to welcome)
        if (prevBtn) {
            prevBtn.style.display = 'flex';

            prevBtn.addEventListener('click', () => {
                this.goToPrevious();
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                this.goToNext();
            });
        }

        this.prevBtn = prevBtn;
        this.nextBtn = nextBtn;
    }

    handleOptionClick(questionId, optionElement) {
        const optionValue = optionElement.dataset.optionValue;
        const selectType = optionElement.dataset.selectType || 'single';
        const partnerKey = optionElement.dataset.partnerKey;
        const isMultiSelect = selectType === 'multi';

        // Handle Q1 and Q2 (single select)
        if (questionId === 'q1' || questionId === 'q2') {
            // Remove selected from all options
            this.questionContainer.querySelectorAll('.furniture-quiz-option').forEach(opt => {
                opt.classList.remove('selected');
            });

            // Add selected to clicked option
            optionElement.classList.add('selected');

            // Store answer
            if (questionId === 'q1') {
                this.answers.q1 = optionValue;
                // Auto-advance to next question if enabled
                if (this.quizData.autoNext) {
                    setTimeout(() => {
                        this.goToNext();
                    }, 300);
                }
            } else if (questionId === 'q2') {
                this.answers.q2 = optionValue;
                this.partnerMapping = optionValue;
                // Update question flow based on Q2 (this will skip questions and auto-advance)
                this.updateQuestionFlow();
            }
        }
        // Handle Q3-Q10 (conditional)
        else {
            const mappingKey = partnerKey || this.partnerMapping;

            if (isMultiSelect) {
                // Multi-select handling
                if (!this.answers[questionId][mappingKey]) {
                    this.answers[questionId][mappingKey] = [];
                }

                const currentAnswers = this.answers[questionId][mappingKey];
                const index = currentAnswers.indexOf(optionValue);

                if (index > -1) {
                    // Deselect
                    currentAnswers.splice(index, 1);
                    optionElement.classList.remove('selected');
                } else {
                    // Select
                    currentAnswers.push(optionValue);
                    optionElement.classList.add('selected');
                }
            } else {
                // Single select handling
                // Remove selected from options in same group
                const groupOptions = partnerKey
                    ? this.questionContainer.querySelectorAll(`[data-partner-key="${partnerKey}"]`)
                    : this.questionContainer.querySelectorAll(`[data-select-type="single"]`);

                groupOptions.forEach(opt => {
                    opt.classList.remove('selected');
                });

                // Add selected to clicked option
                optionElement.classList.add('selected');

                // Store answer
                this.answers[questionId][mappingKey] = optionValue;

                // Check if this is a partner input question that needs both inputs
                if (partnerKey && this.hasPartnerInputs(questionId, this.partnerMapping)) {
                    // Check if both partner inputs are complete
                    const label1 = this.partnerMapping === 'me_and_partner' ? 'Me' : 'Parent 1';
                    const label2 = this.partnerMapping === 'me_and_partner' ? 'My Partner' : 'Parent 2';
                    const key1 = `${this.partnerMapping}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
                    const key2 = `${this.partnerMapping}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

                    // Check if both inputs are answered (after storing current answer)
                    const answer1 = this.answers[questionId][key1];
                    const answer2 = this.answers[questionId][key2];

                    if (answer1 && answer2) {
                        // Both partner inputs complete, auto-advance if enabled
                        if (this.quizData.autoNext) {
                            setTimeout(() => {
                                this.goToNext();
                            }, 300);
                        }
                    }
                } else {
                    // Single select question (not partner inputs), auto-advance immediately if enabled
                    if (this.quizData.autoNext) {
                        setTimeout(() => {
                            this.goToNext();
                        }, 300);
                    }
                }
            }
        }

        // Update navigation
        this.updateNavigation();
    }

    updateQuestionFlow() {
        // Rebuild question flow excluding skipped questions
        this.questionFlow = [];

        if (this.quizData && this.quizData.questions) {
            this.quizData.questions.forEach((q, index) => {
                const questionId = q.id || `q${index + 1}`;

                // Don't skip Q1 and Q2
                if (questionId === 'q1' || questionId === 'q2') {
                    this.questionFlow.push(questionId);
                } else {
                    // Check if this question should be skipped
                    if (!this.shouldSkipQuestion(questionId)) {
                        this.questionFlow.push(questionId);
                    }
                }
            });
        }

        // Advance past Q2 after Q2 selection if auto-next enabled
        if (this.quizData.autoNext) {
            const q2Index = this.questionFlow.indexOf('q2');
            this.currentQuestionIndex = q2Index >= 0 ? q2Index + 1 : 2;
            // Update progress and render next question
            this.updateProgress();
            this.renderQuestion();
            this.updateNavigation();
        } else {
            // Just update navigation to enable the Next button
            this.updateNavigation();
        }
    }

    updateProgress() {

        const total = this.questionFlow.length;
        const current = this.currentQuestionIndex + 1;
        const percent = (current / total) * 100;

        const currentEl = this.querySelector('.quiz-current');
        const totalEl = this.querySelector('.quiz-total');
        const bar = this.querySelector('.furniture-quiz-progress-fill');

        if (currentEl) currentEl.textContent = current;
        if (totalEl) totalEl.textContent = total;
        if (bar) bar.style.width = percent + '%';

    }

    updateNavigation() {
        // Previous button — always enabled (Q1 goes back to welcome)
        if (this.prevBtn) {
            this.prevBtn.disabled = false;
        }

        // Next button
        if (this.nextBtn) {
            const questionId = this.getCurrentQuestionId();
            const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

            // Update button content based on current position
            if (isLastQuestion) {
                // Show "Finish" text on last question
                this.nextBtn.innerHTML = this.quizData.finishText || 'Finish';
                this.nextBtn.classList.add('finish');
            } else {
                // Restore the original button HTML (with icon)
                const defaultButtonHTML = this.nextBtn.getAttribute('data-default-html');
                if (defaultButtonHTML) {
                    this.nextBtn.innerHTML = defaultButtonHTML;
                } else {
                    // Fallback if attribute not found
                    this.nextBtn.innerHTML = this.quizData.nextText || 'Next';
                }
            }

            // Check if current question is answered
            const isAnswered = this.isQuestionAnswered(questionId);
            this.nextBtn.disabled = !isAnswered;
        }
    }

    isQuestionAnswered(questionId) {
        if (questionId === 'q1') {
            return this.answers.q1 !== null;
        }
        if (questionId === 'q2') {
            return this.answers.q2 !== null;
        }

        // For Q3-Q10, check based on partner mapping
        if (this.shouldSkipQuestion(questionId)) {
            return true; // Skipped questions are considered answered
        }

        const mappingKey = this.partnerMapping;
        if (!mappingKey) return false;

        const questionData = this.quizData.questions.find(q => q.id === questionId);
        const selectType = questionData?.selectType || 'single';
        const isMultiSelect = selectType === 'multi';

        // Check if has partner inputs
        if (this.hasPartnerInputs(questionId, mappingKey)) {
            const label1 = mappingKey === 'me_and_partner' ? 'Me' : 'Parent 1';
            const label2 = mappingKey === 'me_and_partner' ? 'My Partner' : 'Parent 2';
            const key1 = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
            const key2 = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

            if (isMultiSelect) {
                const answers1 = this.answers[questionId][key1] || [];
                const answers2 = this.answers[questionId][key2] || [];
                return answers1.length > 0 && answers2.length > 0;
            } else {
                return this.answers[questionId][key1] && this.answers[questionId][key2];
            }
        } else {
            if (isMultiSelect) {
                const answers = this.answers[questionId][mappingKey] || [];
                return answers.length > 0;
            } else {
                return this.answers[questionId][mappingKey] !== undefined &&
                    this.answers[questionId][mappingKey] !== null;
            }
        }
    }

    goToPrevious() {
        if (this.currentQuestionIndex > 0) {
            this.currentQuestionIndex--;
            this.renderQuestion();
            this.updateNavigation();
        } else {
            // At Q1, go back to welcome screen
            this.goBackToWelcome();
        }
    }

    goBackToWelcome() {
        const container = this.querySelector('.furniture-quiz-container');
        if (container) {
            container.classList.add('quiz-not-started');
        }
        const welcomeScreen = this.querySelector('.furniture-quiz-welcome-screen');
        if (welcomeScreen) {
            welcomeScreen.style.display = '';
        }
        this.scrollToQuizTop();
    }

    goBackToQuestions() {
        // Remove concerns container if present
        const concernsContainer = this.querySelector('.furniture-quiz-concerns-container');
        if (concernsContainer) concernsContainer.remove();

        // Remove answers summary container if present
        const summaryContainer = this.querySelector('.furniture-quiz-answers-summary-container');
        if (summaryContainer) summaryContainer.remove();

        // Show quiz container and navigate to last question
        const quizContainer = this.querySelector('.furniture-quiz-container');
        if (quizContainer) {
            quizContainer.style.display = '';
            quizContainer.classList.remove('quiz-not-started');
        }

        // Hide customer form
        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (customerForm) customerForm.style.display = 'none';

        // Show questions section
        const questionsSection = this.querySelector('.furniture-quiz-questions-section');
        if (questionsSection) questionsSection.style.display = '';

        // Set to last question
        this.currentQuestionIndex = this.questionFlow.length - 1;
        this.renderQuestion();
        this.updateNavigation();
        this.scrollToQuizTop();
    }

    goToNext() {
        const questionId = this.getCurrentQuestionId();
        const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

        if (!this.isQuestionAnswered(questionId)) {
            return;
        }

        const questionData = this.quizData.questions.find(q => q.id === questionId);
        // Tracking for drop-off
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'quiz_question_complete',
            question_id: questionId,
            question_index: this.currentQuestionIndex,
            question_text: questionData ? this.getQuestionText(questionId, questionData) : ''
        });

        if (isLastQuestion) {
            this.finishQuiz();
        } else {
            this.currentQuestionIndex++;
            this.renderQuestion();
            this.updateNavigation();
        }
    }

    finishQuiz() {
        // Tracking for quiz complete (before form)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'quiz_complete'
        });

        // If user already submitted form before (retake), skip form and auto-submit
        if (this._savedCustomerInfo) {
            this.showConcernsSummary();
            return;
        }

        if (this.quizData.skipForm) {
            // Skip form: Questions → Concerns → Product Results
            this.showConcernsSummary();
        } else if (this.quizData.showAnswersBeforeForm) {
            // Flow: Questions → Concerns → Form → Product Results
            this.showConcernsSummary();
        } else {
            // Flow: Questions → Form → Concerns → Product Results
            this.showCustomerForm();
        }
    }

    redirectToResults() {
        // Build URL with quiz answers as query parameters
        const url = new URL(this.quizData.resultsUrl, window.location.origin);

        // Add all answers to URL params
        Object.keys(this.answers).forEach(questionId => {
            const answer = this.answers[questionId];
            if (answer !== null && answer !== undefined) {
                if (typeof answer === 'object') {
                    // For conditional questions, stringify the object
                    Object.keys(answer).forEach(key => {
                        const value = answer[key];
                        if (value !== null && value !== undefined) {
                            if (Array.isArray(value)) {
                                url.searchParams.append(`${questionId}_${key}`, value.join(','));
                            } else {
                                url.searchParams.append(`${questionId}_${key}`, value);
                            }
                        }
                    });
                } else {
                    url.searchParams.append(questionId, answer);
                }
            }
        });

        // Add partner mapping
        if (this.partnerMapping) {
            url.searchParams.append('partner_mapping', this.partnerMapping);
        }

        // Redirect
        window.location.href = url.toString();
    }

    async showResults() {
        // Hide quiz container
        const quizContainer = this.querySelector('.furniture-quiz-container');
        if (quizContainer) {
            quizContainer.style.display = 'none';
        }

        // Hide customer form if visible
        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (customerForm) {
            customerForm.style.display = 'none';
        }

        // Show loading state first
        const resultsContainer = document.createElement('div');
        resultsContainer.className = 'furniture-quiz-results-container';
        resultsContainer.innerHTML = this.buildResultsLoadingHTML();
        this.appendChild(resultsContainer);
        this.scrollToQuizTop();

        // Fetch recommended product cards (server-rendered Liquid HTML)
        let productCardsHTML = '';
        try {
            if (this.cachedProductCardsHTML) {
                productCardsHTML = this.cachedProductCardsHTML;
                // Clear it so it's not reused inappropriately on retakes
                this.cachedProductCardsHTML = null;
            } else {
                const shopifyTags = this.buildShopifyTags();
                productCardsHTML = await this.fetchRecommendedProductsHTML(shopifyTags);
            }
        } catch (err) {
            console.error('Discovery process encountered an error:', err);
        }

        // Build full results HTML with server-rendered product cards
        const resultsHTML = this.buildResultsHTML(productCardsHTML);
        resultsContainer.innerHTML = resultsHTML;

        // Store quiz data in sessionStorage
        try {
            sessionStorage.setItem('furniture-quiz-data', JSON.stringify({
                answers: this.answers,
                partnerMapping: this.partnerMapping,
                resultsHTML: resultsHTML
            }));
        } catch (e) {
            console.error('Quiz data persistence encountered an issue:', e);
        }

        // Bind retake quiz buttons
        resultsContainer.querySelectorAll('[data-retake-quiz]').forEach(btn => {
            btn.addEventListener('click', () => {
                this.retakeQuiz();
            });
        });

        // Init horizontal scroll drag for product cards
        this.initProductScroll(resultsContainer);

        // Init size swatch click handlers for collection-style cards
        this.initSwatchHandlers(resultsContainer);
    }

    retakeQuiz() {
        // Remove results container
        const resultsContainer = this.querySelector('.furniture-quiz-results-container');
        if (resultsContainer) {
            resultsContainer.remove();
        }

        // Remove concerns container if present
        const concernsContainer = this.querySelector('.furniture-quiz-concerns-container');
        if (concernsContainer) {
            concernsContainer.remove();
        }

        // Remove answers summary container if present
        const summaryContainer = this.querySelector('.furniture-quiz-answers-summary-container');
        if (summaryContainer) {
            summaryContainer.remove();
        }

        // Reset quiz state
        this.currentQuestionIndex = 0;
        this.answers = {};
        this.partnerMapping = null;
        this._formListenersAttached = false;
        this.questionFlow = [];
        if (this.quizData && this.quizData.questions) {
            this.quizData.questions.forEach((q, index) => {
                this.questionFlow.push(q.id || `q${index + 1}`);
            });
        }
        this.initializeAnswers();

        // Clear pre-fetch cache so retake gets fresh data
        this._prefetchedRawHTML = null;
        this._prefetchPromise = null;

        // Clear session storage
        try {
            sessionStorage.removeItem('furniture-quiz-data');
        } catch (e) {
            // State cleared successfully or already empty
        }

        // Show quiz container and reset to welcome screen
        const quizContainer = this.querySelector('.furniture-quiz-container');
        if (quizContainer) {
            quizContainer.style.display = '';
            quizContainer.classList.add('quiz-not-started');
        }

        // Show welcome screen
        const welcomeScreen = this.querySelector('.furniture-quiz-welcome-screen');
        if (welcomeScreen) {
            welcomeScreen.style.display = '';
        }

        // Hide customer form but do NOT reset — keep verified user info
        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (customerForm) {
            customerForm.style.display = 'none';
        }

        // Reset questions section display (clear any inline style)
        const questionsSection = this.querySelector('.furniture-quiz-questions-section');
        if (questionsSection) {
            questionsSection.style.display = '';
        }

        // Re-render initial question
        this.isInitialRender = true;
        this.renderQuestion();
        this.updateNavigation();

        this.scrollToQuizTop();
    }

    showCustomerForm() {
        // Ensure initialization continuity (handles cases where the discovery flow starts late)
        this._prefetchProductsHTML();

        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (!customerForm) {
            console.error('Customer lead form not initialized');
            // Transition directly to results for optimized user flow
            this.showResults();
            return;
        }

        // Guard against re-attaching event listeners on repeated calls
        if (this._formListenersAttached) {
            // Just show the form without re-attaching listeners
            const quizContainer = this.querySelector('.furniture-quiz-container');
            if (quizContainer) quizContainer.style.display = 'none';
            this.updateQuizDataInNote();
            customerForm.style.display = 'block';
            this.scrollToQuizTop();
            return;
        }
        this._formListenersAttached = true;

        // Hide quiz container
        const quizContainer = this.querySelector('.furniture-quiz-container');
        if (quizContainer) {
            quizContainer.style.display = 'none';
        }

        // Initialize quiz data in the form
        this.updateQuizDataInNote();

        // Show the form
        customerForm.style.display = 'block';
        this.scrollToQuizTop();

        // Inject back button into form if not already present
        const formContainer = customerForm.querySelector('.furniture-quiz-customer-form-container');
        if (formContainer && !formContainer.querySelector('.furniture-quiz-form-back-btn')) {
            const backBtn = document.createElement('button');
            backBtn.type = 'button';
            backBtn.className = 'furniture-quiz-form-back-btn';
            backBtn.innerHTML = this.quizData.concernsBackBtn || '&larr; Back';
            backBtn.addEventListener('click', () => {
                this.goBackToQuestions();
            });
            // Insert after subtitle (p tag), so it appears below the heading
            const subtitle = formContainer.querySelector('.furniture-quiz-customer-form-subtitle');
            if (subtitle && subtitle.nextSibling) {
                formContainer.insertBefore(backBtn, subtitle.nextSibling);
            } else if (subtitle) {
                subtitle.parentNode.appendChild(backBtn);
            } else {
                formContainer.insertBefore(backBtn, formContainer.firstChild);
            }
        }

        // OTP state
        this.otpVerified = false;
        this.otpSent = false;

        const form = customerForm.querySelector('#QuizCustomerForm');
        if (!form) return;

        const phoneInput = this.querySelector('#QuizForm-phone');
        const phoneCountrySelect = this.querySelector('#QuizForm-phone-country');
        const otpCheckBtn = this.querySelector('#QuizForm-otp-check');
        const otpFieldWrapper = this.querySelector('.furniture-quiz-otp-field');
        const otpResendArea = this.querySelector('#QuizForm-otp-resend-area');
        const otpResendBtn = this.querySelector('#QuizForm-otp-resend');
        const otpTimerDisplay = this.querySelector('#QuizForm-otp-timer');
        const otpInput = this.querySelector('#QuizForm-otp');
        const otpStatus = this.querySelector('#QuizForm-otp-status');
        const otpError = this.querySelector('#QuizForm-otp-error');
        const pincodeInput = this.querySelector('#QuizForm-Pincode');

        // Pincode: allow only digits, max 6
        if (pincodeInput) {
            pincodeInput.addEventListener('input', () => {
                pincodeInput.value = pincodeInput.value.replace(/\D/g, '').slice(0, 6);
            });
        }

        const sendOTP = (phone) => {
            if (otpError) { otpError.style.display = 'none'; otpError.textContent = ''; }

            // OTP moved to sh-dfx-tools (brand-pinned SH) via window.SHLead.
            // A failed send now throws into the .catch below instead of silently
            // reporting success and revealing an OTP field no code was sent for.
            (window.SHLead ? window.SHLead.sendOtp(phone) : Promise.reject(new Error('SHLead unavailable'))).then((res) => {

                if (!res.ok) throw new Error(res.message || 'Failed to send OTP');

                this.otpResendAfter = res.resendAfterSeconds;
                console.log('OTP sent to', phone);
                if (otpFieldWrapper) otpFieldWrapper.style.display = '';
                if (otpResendArea) otpResendArea.style.display = 'flex';
                this.otpSent = true;
                this.startOtpTimer(this.otpResendAfter);
            }).catch(err => {
                console.error('OTP transmission unsuccessful:', err);
                if (otpError) {
                    otpError.textContent = this.quizData.errorOtpSendFailed || 'Failed to send OTP. Please try again.';
                    otpError.style.display = 'block';
                }
            });
        };

        // Phone input validation
        if (phoneInput) {
            phoneInput.addEventListener('input', () => {
                const phone = phoneInput.value.trim();
                if (/^[6-9]\d{9}$/.test(phone)) {
                    if (otpCheckBtn) otpCheckBtn.disabled = false;
                } else {
                    if (otpCheckBtn) otpCheckBtn.disabled = true;
                    if (otpFieldWrapper) otpFieldWrapper.style.display = 'none';
                    if (otpResendArea) otpResendArea.style.display = 'none';
                    this.otpSent = false;
                    this.otpVerified = false;
                    if (otpInput) { otpInput.value = ''; otpInput.disabled = false; }
                    if (otpStatus) otpStatus.style.display = 'none';
                    if (this.otpTimer) clearInterval(this.otpTimer);
                }
            });
        }

        // Check button click
        if (otpCheckBtn) {
            otpCheckBtn.addEventListener('click', () => {
                const phone = phoneInput.value.trim();
                sendOTP(phone);
            });
        }

        // Resend button click
        if (otpResendBtn) {
            otpResendBtn.addEventListener('click', () => {
                const phone = phoneInput.value.trim();
                sendOTP(phone);
            });
        }

        // OTP Timer Logic
        this.startOtpTimer = (seconds) => {
            if (this.otpTimer) clearInterval(this.otpTimer);
            if (!otpResendBtn || !otpTimerDisplay) return;

            let timeLeft = parseInt(seconds, 10) || 60;
            otpResendBtn.disabled = true;
            otpTimerDisplay.style.display = 'inline';
            otpTimerDisplay.textContent = `(${timeLeft}s)`;

            this.otpTimer = setInterval(() => {
                timeLeft--;
                if (timeLeft <= 0) {
                    clearInterval(this.otpTimer);
                    otpResendBtn.disabled = false;
                    otpTimerDisplay.style.display = 'none';
                } else {
                    otpTimerDisplay.textContent = `(${timeLeft}s)`;
                }
            }, 1000);
        };

        // OTP input: verify when enough digits entered
        if (otpInput) {
            otpInput.addEventListener('input', () => {
                const otp = otpInput.value.trim();
                const phone = phoneInput ? phoneInput.value.trim() : '';
                if (otpError) { otpError.style.display = 'none'; otpError.textContent = ''; }

                if (otp.length === 6 && !this.otpVerified) {

                    (window.SHLead ? window.SHLead.verifyOtp(phone, otp) : Promise.reject(new Error('SHLead unavailable')))

                        .then(data => {

                            if (data.verified === true) {
                                this.otpVerified = true;

                                try {
                                    if (window.SHLead) window.SHLead.moengage.otpVerified({
                                        phone: phone,
                                        pincode: pincodeInput ? pincodeInput.value.trim() : '',
                                        verification_status: 'Success'
                                    });
                                } catch (e) { console.warn('[quiz] moengage otpVerified failed', e); }

                                if (otpStatus) otpStatus.style.display = 'block';
                                if (otpInput) otpInput.disabled = true;
                                if (otpError) otpError.style.display = 'none';
                                if (otpResendArea) otpResendArea.style.display = 'none';
                                if (otpCheckBtn) otpCheckBtn.style.display = 'none';
                                if (this.otpTimer) clearInterval(this.otpTimer);
                            } else {
                                if (otpError) {
                                    otpError.textContent = this.quizData.errorOtpInvalid || 'Invalid OTP. Please try again.';
                                    otpError.style.display = 'block';
                                }
                            }
                        })
                        .catch(() => {
                            if (otpError) {
                                otpError.textContent = this.quizData.errorOtpFailed || 'Verification failed. Please try again.';
                                otpError.style.display = 'block';
                            }
                        });
                }
            });
        }

        // Form submission handler
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            // Update quiz data
            this.updateQuizDataInNote();

            // Validate required fields
            const firstNameInput = this.querySelector('#QuizForm-FirstName');
            const emailInput = this.querySelector('#QuizForm-email');

            if (!firstNameInput || !firstNameInput.value.trim()) {
                alert(this.quizData.errorFirstNameRequired || 'Please enter your first name');
                return;
            }

            if (!pincodeInput || !pincodeInput.value.trim() || pincodeInput.value.trim().length !== 6) {
                alert(this.quizData.errorPincodeInvalid || 'Please enter a valid 6-digit pincode');
                return;
            }

            // Removed email validation to make it optional

            if (!phoneInput || !phoneInput.value.trim()) {
                alert(this.quizData.errorPhoneRequired || 'Please enter your phone number');
                return;
            }

            // Phone must be verified via OTP
            if (!this.otpVerified) {
                alert(this.quizData.errorOtpRequired || 'Please verify your phone number with OTP before submitting');
                return;
            }

            // Tracking for lead form submission
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'quiz_form_submit',
                email: emailInput ? emailInput.value.trim() : ''
            });

            // Disable submit button
            const submitButton = form.querySelector('button[type="submit"]');
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.textContent = this.quizData.generatingRecommendationsText || 'Generating Recommendations...';
            }

            // Pre-fetch product recommendations to get their URLs and names
            let productUrls = "";
            let productRecommendations = []; // [{name, url}, ...]
            try {
                const shopifyTags = this.buildShopifyTags();
                // Fetch HTML and cache it for the showResults stage
                this.cachedProductCardsHTML = await this.fetchRecommendedProductsHTML(shopifyTags);

                if (this.cachedProductCardsHTML) {
                    // Parse HTML to extract product URLs and names
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(this.cachedProductCardsHTML, 'text/html');

                    // Extract product cards with name + URL
                    const productCards = doc.querySelectorAll('.quiz-product-card-wrap');
                    const seen = new Set();

                    productCards.forEach(card => {
                        const link = card.querySelector('a[href*="/products/"]');
                        const titleEl = card.querySelector('.pro_title_cust');
                        if (link) {
                            const href = link.getAttribute('href');
                            const absoluteUrl = href.startsWith('http') ? href : window.location.origin + href;
                            const urlWithoutParams = absoluteUrl.split('?')[0];
                            if (!seen.has(urlWithoutParams)) {
                                seen.add(urlWithoutParams);
                                productRecommendations.push({
                                    name: titleEl ? titleEl.textContent.trim() : '',
                                    url: urlWithoutParams
                                });

                                this._moeCardExtras(productRecommendations[productRecommendations.length - 1], card);
                            }
                        }
                    });

                    // Fallback: if no cards found, try link-based extraction
                    if (productRecommendations.length === 0) {
                        const productLinks = doc.querySelectorAll('a[href*="/products/"]');
                        productLinks.forEach(link => {
                            const href = link.getAttribute('href');
                            if (href) {
                                const absoluteUrl = href.startsWith('http') ? href : window.location.origin + href;
                                const urlWithoutParams = absoluteUrl.split('?')[0];
                                if (!seen.has(urlWithoutParams)) {
                                    seen.add(urlWithoutParams);
                                    productRecommendations.push({ name: '', url: urlWithoutParams });
                                }
                            }
                        });
                    }

                    productUrls = productRecommendations.map(p => p.url).join(', ');
                }
            } catch (err) {
                console.error('Product discovery during submission encountered an error:', err);
                // Proceed with submission to ensure user data persistence
            }

            if (submitButton) {
                submitButton.textContent = this.quizData.submittingText || 'Submitting...';
            }

            // Build payload
            const countryCode = phoneCountrySelect ? phoneCountrySelect.value : '+91';
            const phoneNumber = phoneInput.value.trim();
            const emailValue = emailInput ? emailInput.value.trim() : '';
            const firstNameValue = firstNameInput.value.trim();
            const pincodeValue = pincodeInput.value.trim();

            // Parse individual product URLs for Lead API
            const productUrlsArray = productUrls ? productUrls.split(', ').filter(Boolean) : [];

            const payload = {
                first_name: firstNameValue,
                pincode: pincodeValue,
                email: emailValue,
                phone: `${countryCode}${phoneNumber}`,
                phone_number: phoneNumber,
                country_code: countryCode,
                pre_starter_role: this.answers.pre_starter_role || '',
                pre_starter_name: this.answers.pre_starter_name || '',
                quiz_answers: this.answers,
                partner_mapping: this.partnerMapping,
                page: window.location.pathname,
                recommended_products: productUrls,
                completed_at: new Date().toISOString(),
                device_name: navigator.userAgent
            };

            // Send data to Google Sheet webhook
            if (this.quizData.googleSheetWebhookUrl) {
                this.submitToGoogleSheet(payload);
            }

            // Send data to Lead Creation API
            this.submitToLeadAPI({
                name: firstNameValue,
                phone: `${countryCode}${phoneNumber}`,
                email: emailValue,
                pincode: pincodeValue,
                productUrlsArray: productUrlsArray,
                productRecommendations: productRecommendations,
                storeName: this.answers.pre_starter_role || '',
                employeeCode: this.answers.pre_starter_name || ''
            });

            // Send data to Bikayi chatbot webhook
            this.submitToBikayiWebhook({
                name: firstNameValue,
                phone: phoneNumber,
                productRecommendations: productRecommendations
            });

            // Save customer info for retake auto-submit
            this._savedCustomerInfo = {
                name: firstNameValue,
                phone: `${countryCode}${phoneNumber}`,
                phoneNumber: phoneNumber,
                countryCode: countryCode,
                email: emailValue,
                pincode: pincodeValue,
                preStarterRole: this.answers.pre_starter_role || '',
                preStarterName: this.answers.pre_starter_name || ''
            };

            // Show success / results immediately without waiting for API calls
            this.showFormSuccess();
        });
    }

    async _autoSubmitWithSavedInfo() {
        const info = this._savedCustomerInfo;
        if (!info) return;

        // Prefetch products for new answers
        this._prefetchProductsHTML();

        let productUrls = '';
        let productRecommendations = [];
        try {
            const shopifyTags = this.buildShopifyTags();
            this.cachedProductCardsHTML = await this.fetchRecommendedProductsHTML(shopifyTags);

            if (this.cachedProductCardsHTML) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(this.cachedProductCardsHTML, 'text/html');
                const productCards = doc.querySelectorAll('.quiz-product-card-wrap');
                const seen = new Set();

                productCards.forEach(card => {
                    const link = card.querySelector('a[href*="/products/"]');
                    const titleEl = card.querySelector('.pro_title_cust');
                    if (link) {
                        const href = link.getAttribute('href');
                        const absoluteUrl = href.startsWith('http') ? href : window.location.origin + href;
                        const urlWithoutParams = absoluteUrl.split('?')[0];
                        if (!seen.has(urlWithoutParams)) {
                            seen.add(urlWithoutParams);
                            productRecommendations.push({
                                name: titleEl ? titleEl.textContent.trim() : '',
                                url: urlWithoutParams
                            });

                            this._moeCardExtras(productRecommendations[productRecommendations.length - 1], card);
                        }
                    }
                });

                productUrls = productRecommendations.map(p => p.url).join(', ');
            }
        } catch (err) {
            console.error('Product discovery during auto-submit encountered an error:', err);
        }

        const productUrlsArray = productUrls ? productUrls.split(', ').filter(Boolean) : [];

        // Build Google Sheet payload with saved info + new answers
        const payload = {
            first_name: info.name,
            pincode: info.pincode,
            email: info.email,
            phone: info.phone,
            phone_number: info.phoneNumber,
            country_code: info.countryCode,
            pre_starter_role: info.preStarterRole,
            pre_starter_name: info.preStarterName,
            quiz_answers: this.answers,
            partner_mapping: this.partnerMapping,
            page: window.location.pathname,
            recommended_products: productUrls,
            completed_at: new Date().toISOString(),
            device_name: navigator.userAgent
        };

        if (this.quizData.googleSheetWebhookUrl) {
            this.submitToGoogleSheet(payload);
        }

        this.submitToLeadAPI({
            name: info.name,
            phone: info.phone,
            email: info.email,
            pincode: info.pincode,
            productUrlsArray: productUrlsArray,
            productRecommendations: productRecommendations,
            storeName: info.preStarterRole || '',
            employeeCode: info.preStarterName || ''
        });

        this.submitToBikayiWebhook({
            name: info.name,
            phone: info.phoneNumber,
            productRecommendations: productRecommendations
        });

        console.log('Auto-submitted with saved customer info (retake)');
    }

    submitToGoogleSheet(payload) {
        if (!this.quizData.googleSheetWebhookUrl) return;

        fetch(this.quizData.googleSheetWebhookUrl, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        }).then(() => {
            console.log('Quiz data sent to Google Sheet');
        }).catch(err => {
            console.error('Error sending quiz data to Google Sheet:', err);
        });
    }

    /* ---------- MoEngage only (2026-09-01) ----------
     * The MoEngage event now carries the top 3 recommendations as flat
     * attributes (product_recommendation_1 ... product_mrp_3). Everything about
     * how they are named, shortened and flattened lives in
     * snippets/sh-lead-moengage.liquid; this pair of helpers only reads the
     * three extra values off the card that {name, url} did not already carry.
     * No existing quiz behaviour, payload or selector changed.
     */
    _moeCardExtras(rec, card) {
        if (!rec || !card) return;
        try {
            const img = card.querySelector('.proimg img');
            const src = img ? (img.getAttribute('src') || '') : '';
            // Shopify CDN srcs are protocol-relative, which is useless in an email or an SMS.
            rec.image = src.indexOf('//') === 0 ? 'https:' + src : src;

            // data-price is the first available variant in paise, the same
            // variant the card prints. The printed price is the fallback.
            const paise = parseInt(card.getAttribute('data-price') || '', 10);
            if (!isNaN(paise)) rec.price = Math.round(paise / 100);
            if (rec.price === undefined) {
                const priceEl = card.querySelector('[id^="product-price-"]');
                if (priceEl) rec.price = this._moeMoney(priceEl.textContent);
            }

            // Only rendered when compare_at_price > price, i.e. on discounted products.
            const compareEl = card.querySelector('[id^="compare-at-price-"]');
            if (compareEl) rec.compareAtPrice = this._moeMoney(compareEl.textContent);
        } catch (e) {
            console.warn('[quiz] moengage card fields failed', e);
        }
    }

    // "₹ 7,169" -> 7169
    _moeMoney(text) {
        const m = String(text == null ? '' : text).replace(/[^\d.]/g, '').match(/\d+(?:\.\d+)?/);
        return m ? Math.round(parseFloat(m[0])) : undefined;
    }

    submitToLeadAPI({ name, phone, email, pincode, productUrlsArray, productRecommendations = [], storeName, employeeCode }) {
        // Build Questions_answer_array from quiz answers
        const questionsAnswerArray = [];
        this.questionFlow.forEach(qId => {
            const questionData = this.quizData.questions.find(q => q.id === qId);
            const questionText = questionData ? (questionData.title || questionData.text || qId) : qId;
            let answerValue = '';

            if (qId === 'q1' || qId === 'q2') {
                answerValue = this.answers[qId] || '';
            } else if (this.answers[qId]) {
                const answerObj = this.answers[qId];
                const values = Object.values(answerObj).filter(v => v !== null && v !== undefined);
                answerValue = values.map(v => Array.isArray(v) ? v.join(', ') : v).join(', ');
            }

            questionsAnswerArray.push({
                question: questionText,
                answer: answerValue
            });
        });

        const leadPayload = {
            name: name,
            phone: phone.replace(/^\+91/, ''),
            email: email,
            pincode: pincode,
            channel: 'Website',
            brand: 'Sleepyhead',
            recommendation1: productUrlsArray[0] || '',
            recommendation2: productUrlsArray[1] || '',
            recommendation3: productUrlsArray[2] || '',
            recommendation4: productUrlsArray[3] || '',
            timestamp: new Date().toISOString(),
            Page_referrer: window.location.origin + window.location.pathname,
            Questions_answer_array: questionsAnswerArray.map(qa => `${qa.question}: ${qa.answer}`).join(', '),
            Products_recommendation_array: productUrlsArray.join(', '),
            User_Agent: navigator.userAgent,
            Store_Name: storeName || '',
            Employee_Code: employeeCode || '',
            event: 'sleep_quiz_recommendation'
        };

        // sh-dfx-tools Lead API — runs ALONGSIDE the Salesforce, Google Sheet and
        // Bikayi targets below. Its own payload shape: the service 400s on unknown
        // top-level keys, so the flat legacy payload cannot be reused.
        if (window.SHLead) {
            const nowIso = new Date().toISOString();
            const answersSummary = questionsAnswerArray
                .map(qa => `${qa.question}: ${qa.answer}`)
                .join(' | ')
                .slice(0, 240);
            window.SHLead.createLead({
                phone: phone,
                lead_status: this.otpVerified ? 'otp_verified' : 'unverified',
                name: name,
                email: email,
                pincode: pincode,
                event_timestamp: nowIso,
                store: storeName || '',
                // Implied by submission against the quiz's privacy line — this
                // surface has no consent tick box.
                consent: {
                    marketing: true,
                    channels: ['sms'],
                    granted_at: nowIso,
                    method: 'implied_submit',
                    ui_surface: 'sleep_quiz',
                    locale: 'en-IN'
                },
                attribution: {
                    source: 'sleep_quiz',
                    event: 'sleep_quiz_recommendation',
                    lead_stage: this.otpVerified ? 'post_otp' : 'pre_otp',
                    page_url: window.location.href,
                    referrer: document.referrer || ''
                },
                products: (productRecommendations || [])
                    .filter(pr => pr && pr.name)
                    .slice(0, 10)
                    .map(pr => ({ name: String(pr.name).slice(0, 256), url: pr.url || '' })),
                custom: {
                    employee_code: employeeCode || '',
                    quiz_answers: answersSummary,
                    recommendation_count: (productUrlsArray || []).length
                }
            }).then(res => {
                if (!res.ok) console.warn('[quiz] sh lead api failed:', res.code, res.errors);
            });

            // MoEngage "Quick Login - Profile Submitted" — quiz completion is the
            // point the shopper hands over name + email.
            try {
                window.SHLead.moengage.profileSubmitted({
                    phone: phone,
                    name: name,
                    email: email,
                    pincode: pincode,
                    // Own event name, same as Duroflex's quiz (2026-09-03). This
                    // replaces popup_type as the way to tell a quiz completion
                    // from a Quick Login popup one, so popup_type is no longer sent.
                    event_name: 'Sleep Quiz - Recommendation',
                    // Flattened into product_recommendation_1..product_mrp_3 by the snippet.
                    products: productRecommendations
                });
            } catch (e) { console.warn('[quiz] moengage profileSubmitted failed', e); }
        }

        fetch('https://dfxstorelocatorapi.azurewebsites.net/api/SleepQuiz/LeadCreation', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': 'TGVhZENyZWF0aW9uU2xlZXBxdWl6'
            },
            body: JSON.stringify(leadPayload)
        })
        .then(res => res.json())
        .then(data => {
            console.log('Lead API response:', data);
            if (data.opportunityId) {
                console.log('Lead created — opportunityId:', data.opportunityId);
            }
        })
        .catch(err => {
            console.error('Error sending data to Lead API:', err);
        });
    }

    submitToBikayiWebhook({ name, phone, productRecommendations }) {
        const bikayiPayload = {
            phone: phone,
            name: name,
            recommend_1: productRecommendations[0] ? productRecommendations[0].name : '',
            recommend_1_url: productRecommendations[0] ? productRecommendations[0].url : '',
            recommend_2: productRecommendations[1] ? productRecommendations[1].name : '',
            recommend_2_url: productRecommendations[1] ? productRecommendations[1].url : '',
            recommend_3: productRecommendations[2] ? productRecommendations[2].name : '',
            recommend_3_url: productRecommendations[2] ? productRecommendations[2].url : ''
        };

        fetch('https://bikapi.bikayi.app/chatbot/webhook/4cPf3HDujyN04Wc6vtYm1sv19tZ2?flow=sleepquiz5920', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bikayiPayload)
        })
        .then(res => res.json())
        .then(data => {
            console.log('Bikayi webhook response:', data);
        })
        .catch(err => {
            console.error('Error sending data to Bikayi webhook:', err);
        });
    }

    updateQuizDataInNote() {
        const quizAnswersField = this.querySelector('#QuizForm-quiz-answers');
        if (!quizAnswersField) return;

        const firstNameInput = this.querySelector('#QuizForm-FirstName');
        const pincodeInput = this.querySelector('#QuizForm-Pincode');
        const phoneInput = this.querySelector('#QuizForm-phone');
        const phoneCountrySelect = this.querySelector('#QuizForm-phone-country');
        const emailInput = this.querySelector('#QuizForm-email');

        const firstName = firstNameInput ? firstNameInput.value.trim() : '';
        const pincode = pincodeInput ? pincodeInput.value.trim() : '';
        const phoneNumber = phoneInput ? phoneInput.value.trim() : '';
        const countryCode = phoneCountrySelect ? phoneCountrySelect.value : '+91';
        const phone = phoneNumber ? `${countryCode}${phoneNumber}` : '';
        const email = emailInput ? emailInput.value.trim() : '';

        // Build quiz data object
        const quizData = {
            partnerMapping: this.partnerMapping,
            answers: this.answers,
            customerInfo: {
                firstName: firstName,
                pincode: pincode,
                email: email,
                phone: phone,
                phoneNumber: phoneNumber,
                countryCode: countryCode,
                preStarterRole: this.answers.pre_starter_role || '',
                preStarterName: this.answers.pre_starter_name || ''
            },
            completedAt: new Date().toISOString()
        };

        // Store as JSON string in hidden textarea
        quizAnswersField.value = JSON.stringify(quizData, null, 2);
    }


    showFormSuccess() {
        // Hide the customer form
        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (customerForm) {
            customerForm.style.display = 'none';
        }

        if (this.quizData.showAnswersBeforeForm) {
            // ON flow: Questions → Concerns → Form → Product Results
            // They already saw concerns before the form, go straight to results
            this.showResults();
        } else {
            // OFF flow: Questions → Form → Concerns → Product Results
            // Form was first, now show concerns screen
            this.showConcernsSummary();
        }
    }

    showConcernsSummary() {
        // Hide quiz container
        const quizContainer = this.querySelector('.furniture-quiz-container');
        if (quizContainer) {
            quizContainer.style.display = 'none';
        }

        // Hide customer form if visible
        const customerForm = this.querySelector('.furniture-quiz-customer-form');
        if (customerForm) {
            customerForm.style.display = 'none';
        }

        // Build concerns HTML
        const concerns = this.collectResultConcerns();

        // Build concern list items
        let concernListHTML = '';
        concerns.forEach(concern => {
            concernListHTML += `


        <li class="furniture-quiz-results-card-item">


          <span class="furniture-quiz-results-card-icon">${concern.icon}</span>


          <span class="furniture-quiz-results-card-label">${concern.label}</span>


        </li>


      `;
        });

        // Fallback if no concerns
        if (concerns.length === 0) {
            concernListHTML = `


        <li class="furniture-quiz-results-card-item">


          <span class="furniture-quiz-results-card-icon">${this.getResultConcernIcon('default')}</span>


          <span class="furniture-quiz-results-card-label">${this.quizData.concernsDefaultMatch || 'Your personalized mattress match'}</span>


        </li>


      `;
        }

        const html = `


      <div class="furniture-quiz-concerns-screen">


        <div class="furniture-quiz-concerns-left">


          <button type="button" class="furniture-quiz-concerns-back-btn furniture-quiz-concerns-back-desktop" data-go-back>${this.quizData.concernsBackBtn || '&larr; Back'}</button>


          <div class="sh-h2">${this.quizData.concernsTitle || 'Thank You!'}</div>


          <p>${this.quizData.concernsSubtitle || 'We\'ve calculated your results.'}</p>


          <button type="button" class="furniture-quiz-concerns-cta" data-see-results>${this.quizData.concernsCtaText || 'See Your Perfect Mattress'}</button>


          <button type="button" class="furniture-quiz-results-retake" data-retake-quiz>${this.quizData.retakeQuizText || 'Retake The Quiz'}</button>


        </div>


        <div class="furniture-quiz-concerns-right">


          <div class="furniture-quiz-results-card">


            <p class="furniture-quiz-results-card-title">${this.quizData.concernsCardTitle || 'Based on what you\'ve answered, you are looking for a mattress that can help with:'}</p>


            <ul class="furniture-quiz-results-card-list">


              ${concernListHTML}


            </ul>


          </div>


          <div class="furniture-quiz-concerns-retake-mobile">


            <button type="button" data-retake-quiz>${this.quizData.retakeQuizText || 'Retake the Quiz'}</button>


          </div>


        </div>


        <div class="furniture-quiz-concerns-bottom-bar">


          <button type="button" class="furniture-quiz-concerns-back-btn" data-go-back>${this.quizData.concernsBackBtn || '&larr; Back'}</button>


          <button type="button" class="furniture-quiz-concerns-bottom-cta" data-see-results>${this.quizData.concernsCtaText || 'See Your Perfect Mattress'}</button>


        </div>


      </div>


    `;

        // Create container
        const concernsContainer = document.createElement('div');
        concernsContainer.className = 'furniture-quiz-concerns-container';
        concernsContainer.innerHTML = html;
        this.appendChild(concernsContainer);
        this.scrollToQuizTop();

        // Bind "See Your Perfect Mattress" buttons
        concernsContainer.querySelectorAll('[data-see-results]').forEach(btn => {
            btn.addEventListener('click', async () => {
                concernsContainer.remove();

                // If user already submitted form before (retake), auto-submit and show results
                if (this._savedCustomerInfo) {
                    await this._autoSubmitWithSavedInfo();
                    this.showResults();
                    return;
                }

                if (this.quizData.skipForm) {
                    // Skip form: go straight to product results
                    this.showResults();
                } else if (this.quizData.showAnswersBeforeForm) {
                    // ON flow: Concerns shown BEFORE form → go to form next
                    this.showCustomerForm();
                } else {
                    // OFF flow: Concerns shown AFTER form → go to product results
                    this.showResults();
                }
            });
        });

        // Bind retake quiz buttons
        concernsContainer.querySelectorAll('[data-retake-quiz]').forEach(btn => {
            btn.addEventListener('click', () => {
                concernsContainer.remove();
                this.retakeQuiz();
            });
        });

        // Bind back button — go back to last question
        concernsContainer.querySelectorAll('[data-go-back]').forEach(btn => {
            btn.addEventListener('click', () => {
                this.goBackToQuestions();
            });
        });
    }

    getResultConcernIcon(label) {
        const lowerLabel = (label || '').toLowerCase();
        const icons = {
            'sleeping hot': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 28c2-3 2-6 0-9s-2-6 0-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M18 28c2-3 2-6 0-9s-2-6 0-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M28 28c2-3 2-6 0-9s-2-6 0-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`,
            'tossing and turning': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 13a10 10 0 0115.5-1.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M25.5 7v5h-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M28 23a10 10 0 01-15.5 1.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M10.5 29v-5h5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
            'snoring': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="15" cy="18" r="8" stroke="currentColor" stroke-width="2.2"/><path d="M12 16.5c0-.8.7-1.5 1.5-1.5s1.5.7 1.5 1.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M13 21h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M25 9h4l-4 4h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M27 18h3l-3 3h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
            'firm back support': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="20" width="30" height="6" rx="2" stroke="currentColor" stroke-width="2.2"/><path d="M6 20v-4a2 2 0 012-2h20a2 2 0 012 2v4" stroke="currentColor" stroke-width="2.2"/><path d="M13 14V10a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2.2"/><line x1="3" y1="26" x2="6" y2="30" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><line x1="33" y1="26" x2="30" y2="30" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`,
            'back pain': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6c-2 4-3 8-2 12s0 8-2 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><circle cx="22" cy="18" r="3" stroke="currentColor" stroke-width="2" fill="none"/><path d="M20 16l4 4M24 16l-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
            'neck pain': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="10" r="5" stroke="currentColor" stroke-width="2.2"/><path d="M15 15v6h6v-6" stroke="currentColor" stroke-width="2.2"/><path d="M12 21h12v8H12z" stroke="currentColor" stroke-width="2.2" rx="1"/><circle cx="25" cy="18" r="2.5" stroke="currentColor" stroke-width="1.8" fill="none"/><path d="M23.5 16.5l3 3M26.5 16.5l-3 3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`,
            'joint pain': `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 8v8a6 6 0 006 6h0a6 6 0 006-6V8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><circle cx="18" cy="22" r="4" stroke="currentColor" stroke-width="2.2"/><path d="M10 28v-4a6 6 0 016-6h0" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M26 28v-4a6 6 0 00-6-6h0" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`
        };

        // Try exact match first
        if (icons[lowerLabel]) return icons[lowerLabel];

        // Try partial match
        for (const [key, icon] of Object.entries(icons)) {
            if (lowerLabel.includes(key) || key.includes(lowerLabel)) return icon;
        }

        // Default icon (checkmark in circle)
        return `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="18" r="12" stroke="currentColor" stroke-width="2.2"/><path d="M12 18l4 4 8-8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    }

    collectResultConcerns() {
        const concerns = [];
        const mappingKey = this.partnerMapping;
        if (!mappingKey) return concerns;

        // Collect answer labels from all answered questions in the flow
        const concernQuestions = this.questionFlow && this.questionFlow.length > 0
            ? this.questionFlow
            : this.quizData.questions.map((q, i) => q.id || `q${i + 1}`);

        concernQuestions.forEach(questionId => {
            const questionData = this.quizData.questions.find(q => q.id === questionId);
            if (!questionData) return;

            // Skip questions that should be skipped for this mapping
            if (this.shouldSkipQuestion(questionId)) return;

            const answerData = this.answers[questionId];
            if (!answerData) return;

            const selectType = questionData.selectType || 'single';
            const isMultiSelect = selectType === 'multi';

            // Extract answer values
            let answerValues = [];

            if (typeof answerData === 'string' || typeof answerData === 'number') {
                answerValues = [String(answerData)];
            } else if (Array.isArray(answerData)) {
                answerValues = [...answerData];
            } else if (this.hasPartnerInputs(questionId, mappingKey)) {
                const label1 = mappingKey === 'me_and_partner' ? 'Me' : 'Parent 1';
                const label2 = mappingKey === 'me_and_partner' ? 'My Partner' : 'Parent 2';
                const key1 = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
                const key2 = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

                if (isMultiSelect) {
                    answerValues = [...(answerData[key1] || []), ...(answerData[key2] || [])];
                } else {
                    if (answerData[key1]) answerValues.push(answerData[key1]);
                    if (answerData[key2]) answerValues.push(answerData[key2]);
                }
            } else {
                if (isMultiSelect) {
                    answerValues = answerData[mappingKey] || [];
                } else {
                    if (answerData[mappingKey]) answerValues = [answerData[mappingKey]];
                }
            }

            // Map values to labels
            const answerLabels = [];
            answerValues.forEach(value => {
                let option = null;
                if (questionData.optionsByMapping && mappingKey) {
                    const options = questionData.optionsByMapping[mappingKey] || questionData.options;
                    option = options.find(opt => (opt.value || opt.label) === value);
                }
                if (!option) {
                    option = questionData.options.find(opt => (opt.value || opt.label) === value);
                }
                const label = option ? option.label : value;
                if (label && !answerLabels.includes(label)) {
                    answerLabels.push(label);
                }
            });

            // Group all answers under one entry per question
            if (answerLabels.length > 0) {
                concerns.push({
                    label: answerLabels.join(', '),
                    icon: this.getResultConcernIcon(answerLabels[0])
                });
            }
        });

        return concerns;
    }

    buildResultsLoadingHTML() {
        return `


      <div class="furniture-quiz-results-wrapper page-width">


      <div class="furniture-quiz-results-screen">


        <div class="furniture-quiz-results-left">


          <div class="sh-h2 quiz-rec-heading">${this.quizData.resultsRecommendationsTitle || 'Our Recommendations'}</div>


          <p class="quiz-rec-subtext">${this.quizData.resultsLoadingText || 'Finding your perfect mattress...'}</p>


        </div>


        <div class="furniture-quiz-results-products">


          <div class="quiz-product-scroll">


            <div class="quiz-product-card-wrap quiz-product-card--skeleton">


              <div class="quiz-product-img-skeleton"></div>


              <div class="quiz-product-text-skeleton"></div>


              <div class="quiz-product-text-skeleton short"></div>


            </div>


            <div class="quiz-product-card-wrap quiz-product-card--skeleton">


              <div class="quiz-product-img-skeleton"></div>


              <div class="quiz-product-text-skeleton"></div>


              <div class="quiz-product-text-skeleton short"></div>


            </div>


            <div class="quiz-product-card-wrap quiz-product-card--skeleton">


              <div class="quiz-product-img-skeleton"></div>


              <div class="quiz-product-text-skeleton"></div>


              <div class="quiz-product-text-skeleton short"></div>


            </div>


          </div>


        </div>


      </div>


      </div>


    `;
    }

    buildResultsHTML(productCardsHTML = '') {
        // If no product cards HTML was returned, show empty state
        if (!productCardsHTML || productCardsHTML.trim() === '') {
            productCardsHTML = `


        <div class="quiz-product-empty">


          <p>${this.quizData.resultsEmptyText || "We're updating our recommendations. Please check back soon!"}</p>


        </div>


      `;
        }

        // Build the results tag URL for "View All" link
        const shopifyTags = this.buildShopifyTags();
        const basePath = this.quizData.collectionBaseUrl || '/collections/all';
        const cleanBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

        const viewAllUrl = shopifyTags.length > 0
            ? `${cleanBasePath}/${shopifyTags.join('+')}`
            : cleanBasePath;

        const html = `


    <div class="furniture-quiz-results-wrapper page-width">


      <div class="furniture-quiz-results-screen">


        <div class="furniture-quiz-results-left">


          <div class="sh-h2 quiz-rec-heading">${this.quizData.resultsRecommendationsTitle || 'Our Recommendations'}</div>


          <p class="quiz-rec-subtext">${this.quizData.resultsHelpText || 'Need more help Choosing the Mattress'}</p>


          <a href="${this.quizData.resultsContactUrl || '/pages/contact'}" class="quiz-rec-contact-btn">${this.quizData.resultsContactText || 'Contact Us'}</a>


          <button type="button" class="furniture-quiz-results-retake" data-retake-quiz>${this.quizData.retakeQuizText || 'Retake The Quiz'}</button>


        </div>


        <div class="furniture-quiz-results-products">


          <div class="quiz-product-scroll">


            ${productCardsHTML}


          </div>


        </div>


        <div class="quiz-rec-mobile-footer">


          <p class="quiz-rec-subtext">${this.quizData.resultsHelpText || 'Need more help Choosing the Mattress'}</p>


          <a href="${this.quizData.resultsContactUrl || '/pages/contact'}" class="quiz-rec-contact-btn">${this.quizData.resultsContactText || 'Contact Us'}</a>


          <button type="button" class="furniture-quiz-results-retake" data-retake-quiz>${this.quizData.retakeQuizText || 'Retake the Quiz'}</button>


        </div>


      </div>


      </div>


    `;

        return html;
    }

    initProductScroll(container) {
        const scrollEl = container.querySelector('.quiz-product-scroll');
        if (!scrollEl) return;

        let isDown = false, startX, scrollLeft;
        scrollEl.addEventListener('mousedown', e => {
            isDown = true;
            scrollEl.classList.add('grabbing');
            startX = e.pageX - scrollEl.offsetLeft;
            scrollLeft = scrollEl.scrollLeft;
        });
        scrollEl.addEventListener('mouseleave', () => { isDown = false; scrollEl.classList.remove('grabbing'); });
        scrollEl.addEventListener('mouseup', () => { isDown = false; scrollEl.classList.remove('grabbing'); });
        scrollEl.addEventListener('mousemove', e => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - scrollEl.offsetLeft;
            scrollEl.scrollLeft = scrollLeft - (x - startX) * 1.5;
        });
    }

    initSwatchHandlers(container) {
        const swatches = container.querySelectorAll('.product-swatch');
        if (!swatches.length) return;

        swatches.forEach(swatch => {
            swatch.addEventListener('click', function () {
                const productId = this.getAttribute('data-product-id');
                const selectedPrice = parseFloat(this.getAttribute('data-price'));
                const selectedCompareAtPrice = parseFloat(this.getAttribute('data-compare-at-price'));
                const variantId = this.getAttribute('data-variant-id');

                const productCard = this.closest('.product-item');
                if (!productCard) return;

                const priceElement = productCard.querySelector(`#product-price-${productId}`);
                const compareAtPriceElement = productCard.querySelector(`#compare-at-price-${productId}`);
                const discountElement = productCard.querySelector(`#discount-percentage-${productId}`);

                // Toggle active class on swatches for this product
                productCard.querySelectorAll(`.product-swatch[data-product-id="${productId}"]`).forEach(el => {
                    el.classList.remove('active');
                });
                this.classList.add('active');

                // Update sale price
                if (priceElement && !isNaN(selectedPrice)) {
                    const formattedPrice = (selectedPrice / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    priceElement.innerHTML = `\u20B9 ${formattedPrice}`;
                }

                // Update compare-at price and discount
                if (!isNaN(selectedCompareAtPrice) && selectedCompareAtPrice > selectedPrice) {
                    if (compareAtPriceElement) {
                        const formattedCompareAtPrice = (selectedCompareAtPrice / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                        compareAtPriceElement.innerHTML = `\u20B9 ${formattedCompareAtPrice}`;
                    }
                    if (discountElement) {
                        const discountPercentage = Math.floor(((selectedCompareAtPrice - selectedPrice) / selectedCompareAtPrice) * 100);
                        discountElement.innerText = discountPercentage > 0 ? `${discountPercentage}% off` : '';
                        discountElement.style.display = discountPercentage > 0 ? 'inline-block' : 'none';
                    }
                } else {
                    if (compareAtPriceElement) compareAtPriceElement.innerText = '';
                    if (discountElement) {
                        discountElement.innerText = '';
                        discountElement.style.display = 'none';
                    }
                }

                // Update product link with variant ID
                if (variantId) {
                    productCard.querySelectorAll(`a[href*="${productId}"], a.st-prod-link`).forEach(link => {
                        const baseUrl = link.href.split('?')[0];
                        link.href = `${baseUrl}?variant=${variantId}`;
                    });
                }
            });
        });

        // Auto-click first swatch for each product to set initial state
        container.querySelectorAll('.product_variant').forEach(variantGroup => {
            const firstSwatch = variantGroup.querySelector('.product-swatch');
            if (firstSwatch) {
                firstSwatch.click();
            }
        });
    }

    buildResultsTag() {
        const tagParts = [];
        const mappingKey = this.partnerMapping;

        if (!mappingKey) {
            console.warn('No partner mapping found, cannot build tag');
            return '';
        }

        // Q1 - Life Stage (single select)
        const q1Data = this.quizData.questions.find(q => q.id === 'q1');
        if (this.answers.q1 && (!q1Data || q1Data.includeInTag !== false)) {
            tagParts.push(`q1:${this.answers.q1}`);
        }

        // Q2 - Partner Mapping (single select)
        const q2Data = this.quizData.questions.find(q => q.id === 'q2');
        if (this.answers.q2 && (!q2Data || q2Data.includeInTag !== false)) {
            tagParts.push(`q2:${this.answers.q2}`);
        }

        // Q3+ - Conditional questions (dynamic, not hardcoded)
        const questionIds = this.quizData.questions
            .map((q, i) => q.id || `q${i + 1}`)
            .filter(id => id !== 'q1' && id !== 'q2');

        questionIds.forEach(questionId => {
            const answer = this.answers[questionId];

            // Debug logging
            console.log(`Processing ${questionId}:`, answer);

            if (!answer || typeof answer !== 'object') {
                console.log(`Skipping ${questionId}: answer is not an object`);
                return;
            }

            const questionData = this.quizData.questions.find(q => q.id === questionId);
            if (!questionData) {
                console.log(`Skipping ${questionId}: question data not found`);
                return;
            }

            // Check if question should be included in tag
            if (questionData.includeInTag === false) {
                console.log(`Skipping ${questionId}: not included in tag`);
                return;
            }

            // Check if question should be skipped
            if (this.shouldSkipQuestion(questionId)) {
                console.log(`Skipping ${questionId}: question should be skipped for ${mappingKey}`);
                return; // Skip this question in the tag
            }

            const selectType = questionData.selectType || 'single';
            const isMultiSelect = selectType === 'multi';

            // Check if has partner inputs
            if (this.hasPartnerInputs(questionId, mappingKey)) {
                const label1 = mappingKey === 'me_and_partner' ? 'Me' : 'Parent 1';
                const label2 = mappingKey === 'me_and_partner' ? 'My Partner' : 'Parent 2';
                const key1 = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
                const key2 = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

                console.log(`${questionId} has partner inputs - key1: ${key1}, key2: ${key2}`);

                const answers1 = isMultiSelect
                    ? (answer[key1] || [])
                    : (answer[key1] ? [answer[key1]] : []);
                const answers2 = isMultiSelect
                    ? (answer[key2] || [])
                    : (answer[key2] ? [answer[key2]] : []);

                console.log(`${questionId} answers1:`, answers1, `answers2:`, answers2);

                // Combine both partner answers
                const allAnswers = [...answers1, ...answers2].filter(a => a !== null && a !== undefined && a !== '');

                if (allAnswers.length > 0) {
                    const answerStr = allAnswers.join(';');
                    tagParts.push(`${questionId}:${answerStr}`);
                    console.log(`Added ${questionId} to tag: ${questionId}:${answerStr}`);
                } else {
                    console.log(`Skipping ${questionId}: no valid answers found`);
                }
            } else {
                // Single set of answers - check answer object structure
                console.log(`${questionId} single answer - mappingKey: ${mappingKey}, answer keys:`, Object.keys(answer));

                const answers = isMultiSelect
                    ? (answer[mappingKey] || [])
                    : (answer[mappingKey] ? [answer[mappingKey]] : []);

                console.log(`${questionId} extracted answers:`, answers);

                // Filter out empty values
                const validAnswers = answers.filter(a => a !== null && a !== undefined && a !== '');

                if (validAnswers.length > 0) {
                    const answerStr = validAnswers.join(';');
                    tagParts.push(`${questionId}:${answerStr}`);
                    console.log(`Added ${questionId} to tag: ${questionId}:${answerStr}`);
                } else {
                    console.log(`Skipping ${questionId}: no valid answers (answers:`, answers, `, validAnswers:`, validAnswers, `)`);
                }
            }
        });

        const finalTag = tagParts.join('|');
        console.log('Final tag built:', finalTag);
        return finalTag;
    }

    /**
     * Build individual Shopify-compatible tags from quiz answers.
     * Returns array like ['q4-side', 'q5-back_side_pain', 'q5-tossing_turning', 'q7-foam', ...]
     */
    buildShopifyTags() {
        const tags = [];
        const mappingKey = this.partnerMapping;
        if (!mappingKey) return tags;

        // Q1 - Life Stage (single value, sanitized for tag format)
        // Respects includeInTag checkbox — if unchecked, Q1 won't affect scoring
        if (this.answers.q1) {
            const q1Data = this.quizData.questions.find(q => q.id === 'q1');
            if (!q1Data || q1Data.includeInTag !== false) {
                const q1Val = this.answers.q1.replace(/-/g, '_').replace(/\+/g, 'plus');
                tags.push('q1-' + q1Val);
            }
        }

        // Q2 - Partner Mapping (single value, already safe format)
        // Respects includeInTag checkbox
        if (this.answers.q2) {
            const q2Data = this.quizData.questions.find(q => q.id === 'q2');
            if (!q2Data || q2Data.includeInTag !== false) {
                tags.push('q2-' + this.answers.q2);
            }
        }

        // All other questions (Q3+) — dynamic, not hardcoded
        const allQuestions = this.quizData.questions
            .map((q, i) => q.id || `q${i + 1}`)
            .filter(id => id !== 'q1' && id !== 'q2');

        allQuestions.forEach(questionId => {
            const answer = this.answers[questionId];
            if (!answer || typeof answer !== 'object') return;

            const questionData = this.quizData.questions.find(q => q.id === questionId);
            if (!questionData) return;
            if (questionData.includeInTag === false) return;
            if (this.shouldSkipQuestion(questionId)) return;

            const selectType = questionData.selectType || 'single';
            const isMultiSelect = selectType === 'multi';
            let answerValues = [];

            if (this.hasPartnerInputs(questionId, mappingKey)) {
                const label1 = mappingKey === 'me_and_partner' ? 'Me' : 'Parent 1';
                const label2 = mappingKey === 'me_and_partner' ? 'My Partner' : 'Parent 2';
                const key1 = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
                const key2 = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;
                if (isMultiSelect) {
                    answerValues = [...(answer[key1] || []), ...(answer[key2] || [])];
                } else {
                    if (answer[key1]) answerValues.push(answer[key1]);
                    if (answer[key2]) answerValues.push(answer[key2]);
                }
            } else {
                if (isMultiSelect) {
                    answerValues = answer[mappingKey] || [];
                } else {
                    if (answer[mappingKey]) answerValues = [answer[mappingKey]];
                }
            }

            // Create individual tags: q5-back_side_pain, q5-tossing_turning, etc.
            // For Q3 (weight), sanitize special chars: <45 -> lt45, >100 -> gt100, hyphens -> underscores
            answerValues
                .filter(a => a !== null && a !== undefined && a !== '')
                .forEach(val => {
                    let sanitized = val;
                    if (questionId === 'q3') {
                        sanitized = val.replace(/</g, 'lt').replace(/>/g, 'gt').replace(/-/g, '_');
                    }
                    const tag = `${questionId}-${sanitized}`;
                    if (!tags.includes(tag)) tags.push(tag);
                });
        });

        // Budget inference: if Q4 not answered and inference is enabled,
        // inject a q4-* tag based on the user's age (Q1) answer
        const hasQ4 = tags.some(t => t.startsWith('q4-'));
        const bi = this.quizData.budgetInference;
        if (!hasQ4 && bi && bi.enabled && this.answers.q1) {
            const ageKey = this.answers.q1.replace(/-/g, '_').replace(/\+/g, 'plus');
            const inferredBudget = bi.mapping && bi.mapping[ageKey];
            if (inferredBudget) {
                tags.push('q4-' + inferredBudget);
                console.log(`Budget inferred from age ${ageKey}: q4-${inferredBudget}`);
            }
        }

        console.log('Shopify tags built:', tags);
        return tags;
    }

    /**
     * Shared helper: enforce exactly 1 Airboost product in the final set of 3 cards.
     *
     * INPUT:  cardElements — array of DOM .quiz-product-card-wrap elements,
     *         already sorted by relevance score (highest first).
     * OUTPUT: innerHTML for 3 product cards — 1 Airboost + 2 non-Airboost,
     *         final positions shuffled so Airboost isn't always first.
     *
     * Airboost detection:
     *  1. data-tags attribute containing "q8-airboost" (primary — set by quiz-api template)
     *  2. href matching a configured airboost product handle (fallback)
     *
     * The top-scoring Airboost and top-scoring non-Airboost cards are chosen
     * (input order preserved from scoring). Only the final 3-card positions
     * are shuffled for variety.
     */
    enforceAirboostLimit(cardElements, originalWrapper) {
        const airboostHandles = Array.isArray(this.quizData.airboostProductHandles)
            ? this.quizData.airboostProductHandles
            : [];

        // Fisher-Yates shuffle
        const shuffleArray = (array) => {
            for (let i = array.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [array[i], array[j]] = [array[j], array[i]];
            }
            return array;
        };

        // Helper: detect if a card is an Airboost product
        const isAirboostCard = (card) => {
            // Method 1: check data-tags
            const rawTags = card.dataset.tags || '';
            if (rawTags) {
                const cardTagSet = new Set(rawTags.split(',').map(t => t.trim()));
                if (cardTagSet.has('q8-airboost')) return true;
            }
            // Secondary identification method: href matching
            if (airboostHandles.length > 0) {
                const links = Array.from(card.querySelectorAll('a'));
                for (const link of links) {
                    const href = link.getAttribute('href');
                    if (!href) continue;
                    if (airboostHandles.some(handle => handle && href.includes(`/products/${handle}`))) {
                        return true;
                    }
                }
            }
            return false;
        };

        // Split into two pools — order is preserved (highest score first)
        const airboostCards = [];
        const otherCards = [];
        cardElements.forEach(card => {
            if (isAirboostCard(card)) {
                airboostCards.push(card);
            } else {
                otherCards.push(card);
            }
        });

        console.log(`enforceAirboostLimit: ${airboostCards.length} Airboost, ${otherCards.length} non-Airboost`);

        const finalCards = [];

        // Pick 1 best-scoring Airboost (already sorted by score, take first)
        if (airboostCards.length > 0) {
            finalCards.push(airboostCards[0]);
        }

        // Fill remaining 2 slots from best-scoring non-Airboost
        for (let i = 0; i < otherCards.length && finalCards.length < 3; i++) {
            finalCards.push(otherCards[i]);
        }

        // If still under 3 (not enough non-Airboost), pad with more Airboost
        // This only happens when the store has very few non-Airboost products
        for (let i = 1; i < airboostCards.length && finalCards.length < 3; i++) {
            finalCards.push(airboostCards[i]);
        }

        // If Airboost selection is not applicable, utilize top 3 global matches
        if (airboostCards.length === 0) {
            console.warn('enforceAirboostLimit: Utilizing top 3 global relevance matches.');
            finalCards.length = 0;
            for (let i = 0; i < cardElements.length && finalCards.length < 3; i++) {
                finalCards.push(cardElements[i]);
            }
        }

        // Helper: extract price from a card element
        const getCardPrice = (card) => {
            // Try data-price on size swatches first
            const swatch = card.querySelector('.product-swatch[data-price]');
            if (swatch) {
                const p = parseFloat(swatch.dataset.price);
                if (!isNaN(p)) return p;
            }
            // Fallback: parse price text from .price-item--sale span
            const priceEl = card.querySelector('.price-item--sale span[id^="product-price-"]');
            if (priceEl) {
                const p = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
                if (!isNaN(p)) return p;
            }
            return 0;
        };

        // Sort: Airboost first, then non-Airboost by price descending (higher price before lower price)
        finalCards.sort((a, b) => {
            const aIsAirboost = isAirboostCard(a) ? 1 : 0;
            const bIsAirboost = isAirboostCard(b) ? 1 : 0;
            if (aIsAirboost !== bIsAirboost) return bIsAirboost - aIsAirboost; // Airboost first
            return getCardPrice(b) - getCardPrice(a); // Then by price descending
        });

        // Build wrapper
        const newWrapper = document.createElement('div');
        newWrapper.className = 'quiz-product-results-wrapper';
        if (originalWrapper) {
            Array.from(originalWrapper.attributes).forEach(attr => {
                newWrapper.setAttribute(attr.name, attr.value);
            });
        }
        newWrapper.dataset.productCount = finalCards.length.toString();
        finalCards.forEach(card => newWrapper.appendChild(card.cloneNode(true)));

        return newWrapper.innerHTML;
    }

    /**
     * Fetch recommended product cards using relevance scoring.
     *
     * HOW IT WORKS (no app required, guaranteed 3+ results):
     *  1. Fetches ALL quiz-eligible products in one request:
     *         /collections/{base}/machine-learning?view=quiz-api
     *  2. Scores each product card by how many of the user's answer tags it has.
     *     Tags are read from the data-tags attribute on each .quiz-product-card-wrap element.
     *     More matching tags = higher relevance score = ranked first.
     *  3. Cards are sorted by score (highest first), then the top candidates go through
     *     enforceAirboostLimit() which picks exactly 1 Airboost + 2 others, shuffled
     *     (only when quizData.enforceAirboostLimit is enabled; otherwise pure top-3 by score).
     *
     * WHY SCORING INSTEAD OF STRICT AND FILTERING:
     *  With 44 products and 11,520 possible answer combinations, strict AND tag filtering
     *  returns 0 products for 42% of combinations. Scoring guarantees results for every
     *  combination and always shows the BEST matching products first.
     *
     * Prerequisites:
     *  - Products tagged with machine-learning + quiz answer tags (q1-26_35, q5-side, q6-back_side_pain etc.)
     *  - Import product_tags_final.csv via Matrixify (Command=MERGE)
     *  - quiz-api template must include data-tags="{{ product.tags | join: ',' }}" on each card
     *  - Collection base URL set in theme settings (e.g. /collections/mattress)
     */
    async fetchRecommendedProductsHTML(shopifyTags) {
        try {
            let html;

            // Use pre-fetched HTML if available (fired at quiz start)
            if (this._prefetchedRawHTML) {
                console.log('Using pre-fetched product HTML (0ms network wait) ✓');
                html = this._prefetchedRawHTML;
            } else if (this._prefetchPromise) {
                // Pre-fetch is still in flight — wait for it instead of starting a new request
                console.log('Waiting for in-flight pre-fetch to complete...');
                await this._prefetchPromise;
                html = this._prefetchedRawHTML;
            }

            // Discovery flow: initiate fresh fetch if cached state is unavailable
            if (!html) {
                const basePath = this.quizData.collectionBaseUrl || '/collections/all';
                const cleanBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
                const url = `${cleanBasePath}/machine-learning?view=quiz-api`;
                console.log('Fetching all quiz products (no pre-fetch cache):', url);

                const response = await fetch(url);
                if (!response.ok) {
                    console.warn(`Fetch failed (${response.status}): ${url}`);
                    return '';
                }
                html = await response.text();
            }

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const wrapper = tempDiv.querySelector('.quiz-product-results-wrapper');

            if (!wrapper) {
                console.warn('No .quiz-product-results-wrapper found in response.');
                return '';
            }

            const allCards = Array.from(wrapper.querySelectorAll('.quiz-product-card-wrap'));
            if (allCards.length === 0) {
                console.warn('No product cards found.');
                return '';
            }

            // Build user's answer tags for scoring — dynamic, based on all questions
            // Only questions with includeInTag=true AND not skipped generate tags
            const allAnswerTags = this.buildShopifyTags();
            const allQuestionIds = new Set(this.quizData.questions.map((q, i) => q.id || `q${i + 1}`));
            // Also include inferred question IDs (e.g. q4 from budget inference)
            // so inferred tags aren't filtered out
            allAnswerTags.forEach(tag => {
                const qId = tag.split('-')[0];
                if (/^q\d+$/.test(qId)) allQuestionIds.add(qId);
            });
            const scoreTags = allAnswerTags.filter(tag => {
                const qId = tag.split('-')[0];
                return allQuestionIds.has(qId);
            });

            console.log('Scoring tags:', scoreTags);

            // Score each card by tag overlap
            const scored = allCards.map(card => {
                const rawTags = card.dataset.tags || '';
                const cardTagSet = new Set(rawTags.split(',').map(t => t.trim()));
                let score = 0;
                for (const tag of scoreTags) {
                    if (cardTagSet.has(tag)) score++;
                }
                return { card, score };
            });

            // Sort by score descending (best matches first)
            scored.sort((a, b) => b.score - a.score);

            const topScore = scored[0] ? scored[0].score : 0;
            const matchCount = scored.filter(s => s.score > 0).length;
            console.log(`${allCards.length} products scored. Top score: ${topScore}/${scoreTags.length}. Products with score>0: ${matchCount}`);

            // Take scored cards (sorted by relevance)
            const rankedCards = scored.map(s => s.card);

            // If Airboost enforcement is enabled, pick 1 Airboost + 2 others
            // Otherwise, just take the top 3 by score
            if (this.quizData.enforceAirboostLimit !== false) {
                return this.enforceAirboostLimit(rankedCards, wrapper);
            }

            // Pure score-based: take top 3
            const topCards = rankedCards.slice(0, 3);
            const newWrapper = document.createElement('div');
            newWrapper.className = 'quiz-product-results-wrapper';
            if (wrapper) {
                Array.from(wrapper.attributes).forEach(attr => {
                    newWrapper.setAttribute(attr.name, attr.value);
                });
            }
            newWrapper.dataset.productCount = topCards.length.toString();
            topCards.forEach(card => newWrapper.appendChild(card.cloneNode(true)));
            return newWrapper.innerHTML;

        } catch (err) {
            console.error('Error fetching recommendations:', err);
            return '';
        }
    }

    buildResultSectionHTML(section) {
        const questionId = section.id;
        const questionData = this.quizData.questions.find(q => q.id === questionId);
        if (!questionData) return '';

        let html = `<div class="furniture-quiz-results-row">


      <div class="furniture-quiz-results-cell furniture-quiz-results-label">${section.label}</div>


    `;

        // Check if should skip
        if (section.skipFor && section.skipFor.includes(this.partnerMapping)) {
            const skipText = this.quizData.resultsSkipText || 'SKIP';
            html += `<div class="furniture-quiz-results-cell furniture-quiz-results-skip">${skipText}</div></div>`;
            return html;
        }

        // Get answers for this question
        const mappingKey = this.partnerMapping;
        const selectType = questionData.selectType || 'single';
        const isMultiSelect = selectType === 'multi';

        if (this.hasPartnerInputs(questionId, mappingKey)) {
            // Partner/parent inputs
            const label1 = mappingKey === 'me_and_partner' ? 'Me' : 'Parent 1';
            const label2 = mappingKey === 'me_and_partner' ? 'My Partner' : 'Parent 2';
            const key1 = `${mappingKey}_${label1.toLowerCase().replace(/\s+/g, '_')}`;
            const key2 = `${mappingKey}_${label2.toLowerCase().replace(/\s+/g, '_')}`;

            const answers1 = isMultiSelect
                ? (this.answers[questionId][key1] || [])
                : (this.answers[questionId][key1] ? [this.answers[questionId][key1]] : []);
            const answers2 = isMultiSelect
                ? (this.answers[questionId][key2] || [])
                : (this.answers[questionId][key2] ? [this.answers[questionId][key2]] : []);

            html += `<div class="furniture-quiz-results-cell">


        <div class="furniture-quiz-results-partner-group">


          <div class="furniture-quiz-results-partner-label">${label1}</div>


          <div class="furniture-quiz-results-values">${this.formatAnswers(answers1, questionData)}</div>


        </div>


        <div class="furniture-quiz-results-partner-group">


          <div class="furniture-quiz-results-partner-label">${label2}</div>


          <div class="furniture-quiz-results-values">${this.formatAnswers(answers2, questionData)}</div>


        </div>


      </div>`;
        } else {
            // Single set of answers
            const answers = isMultiSelect
                ? (this.answers[questionId][mappingKey] || [])
                : (this.answers[questionId][mappingKey] ? [this.answers[questionId][mappingKey]] : []);

            html += `<div class="furniture-quiz-results-cell">${this.formatAnswers(answers, questionData)}</div>`;
        }

        html += `</div>`;
        return html;
    }

    formatAnswers(answerValues, questionData) {
        if (!answerValues || answerValues.length === 0) {
            return '—';
        }

        // Find option labels for values
        const labels = answerValues.map(value => {
            let option = null;

            // Check optionsByMapping first
            if (questionData.optionsByMapping && this.partnerMapping) {
                const options = questionData.optionsByMapping[this.partnerMapping] || questionData.options;
                option = options.find(opt => (opt.value || opt.label) === value);
            }

            // Final retrieval from default options mapping
            if (!option) {
                option = questionData.options.find(opt => (opt.value || opt.label) === value);
            }

            return option ? option.label : value;
        });

        return labels.join(', ');
    }

    scrollToTop() {
        this.scrollToQuizTop();
    }

    /**
     * DEBUG MODE — test pre-fetch + scoring without filling the form.
     *
     * Usage from browser console:
     *   debugQuiz()                              // uses default test answers
     *   debugQuiz({q5:'back', q8:'airboost'})    // override specific answers
     *   debugQuiz({q5:'side', q6:'back_side_pain', q7:'yes', q8:'foam', q9:'new', q10:'medium'})
     *
     * What it does:
     *   1. Fires the pre-fetch (or uses cache if already fetched)
     *   2. Parses all product cards + data-tags from the response
     *   3. Scores every product against the provided answer tags
     *   4. Applies Airboost enforcement (1 Airboost + 2 others)
     *   5. Logs full scoring table + final 3 picks to console
     *   6. Injects the result cards into the page so you can SEE them
     *
     * No form submission, no OTP, no webhook — pure frontend test.
     */
    async debugRecommendations(overrides = {}) {
        const defaults = {
            q1: '26_35', q2: 'me', q3: '46_70', q4: '10k_25k',
            q5: 'side', q6: 'back_side_pain', q7: 'too_hot',
            q8: 'foam', q9: 'superior_back_support', q10: 'medium_firm_f6_f8'
        };
        const answers = { ...defaults, ...overrides };

        // Apply skip logic: if Q2 is my_children or guests, skip Q3/Q5/Q6/Q7
        const q2Val = answers.q2;
        const skippedQs = new Set();
        if (q2Val === 'my_children' || q2Val === 'guests') {
            // Match the skipFor config from quiz.liquid
            const skipQuestionIds = [];
            if (this.quizData && this.quizData.questions) {
                this.quizData.questions.forEach((q, i) => {
                    const qId = q.id || `q${i + 1}`;
                    if (q.skipFor && q.skipFor.includes(q2Val)) {
                        skipQuestionIds.push(qId);
                    }
                });
            }
            // Fallback if quizData not available
            if (skipQuestionIds.length === 0) {
                skipQuestionIds.push('q3', 'q5', 'q6', 'q7');
            }
            skipQuestionIds.forEach(qId => skippedQs.add(qId));
        }

        // Build score tags only from active (non-skipped) questions
        const scoreTags = Object.entries(answers)
            .filter(([qid]) => !skippedQs.has(qid))
            .map(([qid, val]) => `${qid}-${val}`);

        console.log('%c[DEBUG QUIZ] Starting recommendation test', 'color:#00b894;font-weight:bold');
        if (skippedQs.size > 0) {
            console.log(`[DEBUG QUIZ] Q2=${q2Val} — Skipping: ${[...skippedQs].join(', ')} (${scoreTags.length} active questions)`);
        }
        console.log('[DEBUG QUIZ] Answer tags:', scoreTags.join(' + '));

        // Step 1: Pre-fetch
        console.time('[DEBUG QUIZ] Pre-fetch');
        this._prefetchProductsHTML();
        if (this._prefetchPromise) await this._prefetchPromise;
        console.timeEnd('[DEBUG QUIZ] Pre-fetch');

        if (!this._prefetchedRawHTML) {
            console.error('[DEBUG QUIZ] Pre-fetch returned no HTML. Check the quiz-api template.');
            return;
        }

        console.log(`[DEBUG QUIZ] Raw HTML cached: ${this._prefetchedRawHTML.length.toLocaleString()} chars`);

        // Step 2: Parse cards
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this._prefetchedRawHTML;
        const wrapper = tempDiv.querySelector('.quiz-product-results-wrapper');

        if (!wrapper) {
            console.error('[DEBUG QUIZ] .quiz-product-results-wrapper NOT FOUND in response.');
            return;
        }

        const allCards = Array.from(wrapper.querySelectorAll('.quiz-product-card-wrap'));
        console.log(`[DEBUG QUIZ] Products found: ${allCards.length} (data-product-count: ${wrapper.dataset.productCount})`);

        if (allCards.length === 0) {
            console.error('[DEBUG QUIZ] No .quiz-product-card-wrap elements found.');
            return;
        }

        // Step 3: Check data-tags
        const cardsWithTags = allCards.filter(c => c.dataset.tags && c.dataset.tags.trim() !== '');
        if (cardsWithTags.length === 0) {
            console.warn('[DEBUG QUIZ] WARNING: No cards have data-tags attribute!');
            console.warn('[DEBUG QUIZ] Scoring will be 0 for all products.');
            console.warn('[DEBUG QUIZ] Fix: Add data-tags="{{ product.tags | join: \',\' }}" to quiz-api template.');
        } else {
            console.log(`[DEBUG QUIZ] Cards with data-tags: ${cardsWithTags.length}/${allCards.length}`);
        }

        // Step 4: Score
        const scored = allCards.map(card => {
            const rawTags = card.dataset.tags || '';
            const cardTagSet = new Set(rawTags.split(',').map(t => t.trim()));
            let score = 0;
            const matchedTags = [];
            for (const tag of scoreTags) {
                if (cardTagSet.has(tag)) { score++; matchedTags.push(tag); }
            }
            const titleEl = card.querySelector('.pro_title_cust');
            const linkEl = card.querySelector('a[href*="/products/"]');
            const title = titleEl ? titleEl.textContent.trim() : 'Unknown';
            const handle = linkEl ? linkEl.getAttribute('href').split('/products/')[1]?.split('?')[0] : 'unknown';
            const isAirboost = cardTagSet.has('q8-airboost') || (linkEl && linkEl.getAttribute('href')?.includes('airboost'));
            return { card, score, matchedTags, title, handle, isAirboost, totalTags: cardTagSet.size };
        });

        scored.sort((a, b) => b.score - a.score);

        // Step 5: Log scoring table
        console.log(`\n%c[DEBUG QUIZ] SCORING TABLE (${scoreTags.length} possible points)`, 'color:#0984e3;font-weight:bold');
        console.table(scored.map(s => ({
            handle: s.handle,
            score: `${s.score}/${scoreTags.length}`,
            airboost: s.isAirboost ? 'YES' : '',
            matched: s.matchedTags.join(', ') || '(none)',
            totalTags: s.totalTags
        })));

        // Step 6: Airboost enforcement (respects enforceAirboostLimit setting)
        const airboostEnabled = this.quizData.enforceAirboostLimit !== false;
        const final = [];

        if (airboostEnabled) {
            const airboostPool = scored.filter(s => s.isAirboost);
            const otherPool = scored.filter(s => !s.isAirboost);

            if (airboostPool.length > 0) final.push(airboostPool[0]);
            for (const s of otherPool) { if (final.length >= 3) break; final.push(s); }
            for (let i = 1; i < airboostPool.length && final.length < 3; i++) final.push(airboostPool[i]);
            if (airboostPool.length === 0) {
                final.length = 0;
                for (let i = 0; i < scored.length && final.length < 3; i++) final.push(scored[i]);
            }
        } else {
            // Pure score-based top 3
            for (let i = 0; i < scored.length && final.length < 3; i++) final.push(scored[i]);
        }

        const pickLabel = airboostEnabled ? '1 Airboost + 2 others' : 'Pure score-based top 3';
        console.log(`\n%c[DEBUG QUIZ] FINAL 3 PICKS (${pickLabel})`, 'color:#00b894;font-weight:bold');
        final.forEach((s, i) => {
            console.log(`  ${i + 1}. ${s.handle} (score: ${s.score}/${scoreTags.length}) ${s.isAirboost ? '[AIRBOOST]' : ''}`);
        });

        // Step 7: Inject visible results into page
        const existingDebug = document.getElementById('quiz-debug-results');
        if (existingDebug) existingDebug.remove();

        const debugContainer = document.createElement('div');
        debugContainer.id = 'quiz-debug-results';
        debugContainer.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;background:rgba(0,0,0,0.85);overflow-y:auto;padding:20px;font-family:system-ui,sans-serif;color:#fff;';

        let debugHTML = `


            <div style="max-width:1200px;margin:0 auto;">


                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">


                    <div class="sh-h2" style="margin:0;color:#00b894;">DEBUG: Quiz Recommendations</div>


                    <button onclick="document.getElementById('quiz-debug-results').remove()"


                            style="background:#e74c3c;color:#fff;border:none;padding:8px 16px;border-radius:4px;cursor:pointer;font-size:14px;">


                        Close Debug


                    </button>


                </div>


                <div style="background:#2d3436;padding:12px;border-radius:8px;margin-bottom:16px;">


                    <strong>Answer Tags:</strong> ${scoreTags.join(' + ')}<br>


                    ${skippedQs.size > 0 ? `<strong>Skipped (Q2=${q2Val}):</strong> <span style="color:#fdcb6e">${[...skippedQs].join(', ')}</span><br>` : ''}


                    <strong>Active Questions:</strong> ${scoreTags.length}/${Object.keys(answers).length} |


                    <strong>Products Found:</strong> ${allCards.length} |


                    <strong>With data-tags:</strong> ${cardsWithTags.length} |


                    <strong>Top Score:</strong> ${scored[0]?.score || 0}/${scoreTags.length} |


                    <strong>Airboost Limit:</strong> ${airboostEnabled ? 'ON' : 'OFF'}


                </div>


                <div class="sh-h3" style="color:#74b9ff;">Final 3 Recommendations:</div>


                <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:24px;">


        `;

        final.forEach(s => {
            const imgEl = s.card.querySelector('img');
            const imgSrc = imgEl ? imgEl.getAttribute('src') : '';
            const priceEl = s.card.querySelector('.pricearea');
            const priceHTML = priceEl ? priceEl.innerHTML : '';

            debugHTML += `


                <div style="background:#fff;color:#333;border-radius:8px;overflow:hidden;width:300px;${s.isAirboost ? 'border:3px solid #00b894;' : ''}">


                    ${s.isAirboost ? '<div style="background:#00b894;color:#fff;text-align:center;padding:4px;font-size:12px;font-weight:bold;">AIRBOOST</div>' : ''}


                    ${imgSrc ? `<img src="${imgSrc}" style="width:100%;height:200px;object-fit:contain;background:#f5f5f5;">` : ''}


                    <div style="padding:12px;">


                        <div style="font-weight:bold;margin-bottom:4px;">${s.title}</div>


                        <div style="font-size:13px;color:#666;margin-bottom:4px;">/${s.handle}</div>


                        <div style="font-size:14px;">${priceHTML}</div>


                        <div style="margin-top:8px;padding:6px;background:#dfe6e9;border-radius:4px;font-size:12px;">


                            Score: <strong>${s.score}/${scoreTags.length}</strong> |


                            Matched: ${s.matchedTags.join(', ') || 'none'}


                        </div>


                    </div>


                </div>


            `;
        });

        debugHTML += `</div>`;

        // Full scoring table
        debugHTML += `<div class="sh-h3" style="color:#74b9ff;">Full Scoring Table (${allCards.length} products):</div>`;
        debugHTML += `<table style="width:100%;border-collapse:collapse;font-size:13px;">`;
        debugHTML += `<tr style="background:#2d3436;"><th style="padding:6px;text-align:left;">Handle</th><th>Score</th><th>Airboost</th><th>Matched Tags</th></tr>`;
        scored.forEach((s, i) => {
            const bg = i < 3 ? '#2d3436' : (i % 2 === 0 ? '#1e272e' : 'transparent');
            const highlight = final.includes(s) ? 'border-left:3px solid #00b894;' : '';
            debugHTML += `<tr style="background:${bg};${highlight}">


                <td style="padding:4px 6px;">${s.handle}</td>


                <td style="text-align:center;">${s.score}/${scoreTags.length}</td>


                <td style="text-align:center;">${s.isAirboost ? 'YES' : ''}</td>


                <td style="padding:4px 6px;font-size:11px;">${s.matchedTags.join(', ') || '-'}</td>


            </tr>`;
        });
        debugHTML += `</table></div>`;

        debugContainer.innerHTML = debugHTML;
        document.body.appendChild(debugContainer);

        console.log('%c[DEBUG QUIZ] Debug overlay injected. Close with the red button or press Escape.', 'color:#fdcb6e');

        // Escape key to close
        const escHandler = (e) => {
            if (e.key === 'Escape') {
                const el = document.getElementById('quiz-debug-results');
                if (el) el.remove();
                document.removeEventListener('keydown', escHandler);
            }
        };
        document.addEventListener('keydown', escHandler);

        return { scoreTags, allCards: allCards.length, cardsWithTags: cardsWithTags.length, topScore: scored[0]?.score, final: final.map(s => s.handle) };
    }
}

// Register the custom element
if (!customElements.get('furniture-quiz')) {
    customElements.define('furniture-quiz', FurnitureQuiz);
}

/**
 * Global function to open the furniture quiz popup from any element.
 * Usage: <button onclick="openFurnitureQuiz()">Take the Quiz</button>
 * Works even if the popup trigger was dismissed or session-closed.
 */
window.openFurnitureQuiz = function () {
    const quizEl = document.querySelector('furniture-quiz[data-popup-mode]');
    if (!quizEl) {
        console.warn('openFurnitureQuiz: No popup-mode furniture-quiz element found on this page.');
        return;
    }

    // Ensure popupOverlay is resolved even if initPopupMode skipped it
    if (!quizEl.popupOverlay) {
        const sectionId = quizEl.dataset.sectionId;
        quizEl.popupOverlay = document.getElementById(`QuizPopupOverlay-${sectionId}`);
    }

    if (!quizEl.popupOverlay) {
        console.warn('openFurnitureQuiz: Quiz popup overlay element not found.');
        return;
    }

    quizEl.openPopup();
};

/**
 * Global debug function — test recommendations without filling the form.
 *
 * Usage (paste in browser console on quiz page):
 *
 *   debugQuiz()
 *   debugQuiz({ q1: '36_45', q2: 'me_and_partner', q3: '71_100', q4: '25k_50k', q5: 'back', q8: 'airboost', q10: 'firm' })
 *   debugQuiz({ q5: 'side', q6: 'back_side_pain', q7: 'yes', q8: 'foam', q9: 'new', q10: 'medium' })
 *
 * Shows: full scoring table + final 3 product cards in a visual overlay.
 * No form, no OTP, no webhook — pure frontend test.
 */
window.debugQuiz = function (overrides) {
    const quizEl = document.querySelector('furniture-quiz');
    if (!quizEl) {
        console.error('debugQuiz: No <furniture-quiz> element found on this page.');
        return;
    }
    return quizEl.debugRecommendations(overrides || {});
};